import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download, Users, Inbox, Calendar, Mail, BookOpen, Image as ImageIcon, Settings as SettingsIcon, Upload, Trash2, Copy, Check, GraduationCap, ClipboardList, FileText, Plus, X, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AdminUsersTab from "@/components/admin/AdminUsersTab";
import AdminShell from "@/components/admin/AdminShell";
import EmailPreviewModal from "@/components/admin/EmailPreviewModal";
import PendingSubmissions from "@/components/admin/PendingSubmissions";
import { CancelRegistrationButton } from "@/components/admin/CancelRegistrationModal";

// Section slug (URL param) → internal tab id used by the switch below.
// Mirrors AdminShell's ADMIN_NAV so a URL like /admin/comp-access resolves
// to the CompAccessTab component.
const SLUG_TO_TAB = {
  "": "overview",
  "users": "users",
  "bookings": "bookings",
  "registrations": "registrations",
  "event-registrations": "event_registrations",
  "inquiries": "inquiries",
  "contact": "contact",
  "newsletter": "newsletter",
  "offerings": "offerings",
  "courses": "courses",
  "comp-access": "comp_access",
  "journal": "journal",
  "photos": "photos",
  "settings": "settings",
};

const TAB_TITLES = {
  overview: { title: "Pending Course Submissions", subtitle: "Student course work waiting for your review. Mark each one reviewed and it leaves this list." },
  users: { title: "Users", subtitle: "Everyone with an Aviana account — students, alumni, admins." },
  bookings: { title: "Retreat Bookings", subtitle: "Every retreat and workshop booking processed through Stripe." },
  registrations: { title: "Training & Retreat Registrations", subtitle: "Teacher trainings, destination retreats, and day retreats — set tuition, log payments, send balance invoices." },
  event_registrations: { title: "Event Registrations", subtitle: "Every sister circle · workshop · day-event registrant with contact details and waiver status." },
  inquiries: { title: "Studio Inquiries", subtitle: "Host-a-training & private retreat leads from your studio partners." },
  contact: { title: "Contact Messages", subtitle: "Public contact-form submissions." },
  newsletter: { title: "Newsletter", subtitle: "Beehiiv-synced subscribers with source and country breakdowns." },
  offerings: { title: "Offerings", subtitle: "Retreats, trainings, workshops, and courses shown on the site." },
  courses: { title: "Courses & Quizzes", subtitle: "LearnDash-imported course tree — jump into quiz editing." },
  comp_access: { title: "Grant Course Access", subtitle: "Give a student complimentary or extended access to any LMS course." },
  journal: { title: "Journal", subtitle: "Long-form posts and articles for the blog." },
  photos: { title: "Photo Library", subtitle: "Upload brand photos and reuse them anywhere in the site." },
  settings: { title: "Settings", subtitle: "Site-wide configuration and your admin account." },
};

// Resize/compress an image client-side so phone-camera shots fit under the
// backend 12 MB limit. Long edge max 2400px, JPEG @ 0.85. GIFs pass through untouched.
async function compressImage(file, maxEdge = 2400, quality = 0.85) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  if (file.size <= 1.8 * 1024 * 1024) return file; // already small enough, skip work

  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });

  let { width, height } = img;
  const longest = Math.max(width, height);
  if (longest > maxEdge) {
    const scale = maxEdge / longest;
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob) return file;
  const base = (file.name || "photo").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { section } = useParams();
  const tab = SLUG_TO_TAB[section || ""] ?? null;
  const [data, setData] = useState({ bookings: [], inquiries: [], newsletter: [], offerings: [] });

  useEffect(() => {
    if (user === false) { nav("/login", { state: { from: "/admin" } }); return; }
    if (!user) return;
    if (user.role !== "admin") { nav("/dashboard"); return; }
    (async () => {
      const [b, i, n, o] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/training-inquiries"),
        api.get("/admin/newsletter"),
        api.get("/offerings?include_past=true&include_cancelled=true&include_archived=true"),
      ]);
      setData({ bookings: b.data, inquiries: i.data, newsletter: n.data, offerings: o.data });
    })();
  }, [user, nav]);

  if (!user || user === false) return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;

  // Unknown URL slug → send Holly back to the dashboard rather than a blank page.
  if (tab === null) return <Navigate to="/admin" replace />;

  const exportCsv = () => {
    const header = "email,source,created_at\n";
    const rows = data.newsletter.map(n => `"${n.email}","${n.source || ""}","${n.created_at}"`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `aviana-newsletter-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const downloadWpExport = async (filename) => {
    try {
      const res = await api.get(`/admin/wp-export/${filename}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Downloaded — check your Downloads folder.");
    } catch {
      toast.error("Download failed — try again.");
    }
  };

  const heading = TAB_TITLES[tab] || TAB_TITLES.overview;

  return (
    <AdminShell>
      <div className="max-w-[1400px]" data-testid={`admin-section-${tab}`}>
        {tab === "overview" && <p className="overline mb-2">Welcome back</p>}
        <h1 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">{heading.title}.</h1>
        <p className="text-aviana-muted text-[14px] mt-3 max-w-2xl">{heading.subtitle}</p>

        <div className="mt-8">
          {tab === "overview" && (
            <div data-testid="admin-overview"><PendingSubmissions /></div>
          )}

          {tab === "bookings" && (
            <Table headers={["Date", "Customer", "Offering", "Type", "Amount", "Status", ""]} testid="admin-bookings"
              rows={data.bookings.map(b => [
                new Date(b.created_at).toLocaleDateString(),
                b.user_email || "—",
                b.offering_title,
                b.offering_type,
                `$${b.amount_paid_usd}/${b.total_due_usd}${b.payment_type === "deposit" ? " deposit" : ""}`,
                <Badge key="s" status={b.status} />,
                b.status !== "cancelled" && b.offering_type !== "course"
                  ? <CancelRegistrationButton key="c" source="booking" id={b.id} label="Cancel"
                      onDone={async () => { const r = await api.get("/admin/bookings"); setData((d) => ({ ...d, bookings: r.data })); }} />
                  : null,
              ])}
            />
          )}

          {tab === "inquiries" && (
            <div className="space-y-4" data-testid="admin-inquiries">
              {data.inquiries.length === 0 ? (
                <p className="text-aviana-muted">No inquiries yet. Drop the "Host a Training" link in your Instagram bio.</p>
              ) : data.inquiries.map(i => (
                <div key={i.id} className="card-aviana p-7" data-testid={`inquiry-${i.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                    <div>
                      <h3 className="font-display text-2xl text-aviana-ink">{i.studio_name}</h3>
                      <p className="text-sm text-aviana-muted">{i.contact_name} · {i.email}{i.phone && ` · ${i.phone}`}</p>
                    </div>
                    <Badge status={i.status || "new"} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-aviana-inkSoft">
                    <p><span className="overline-plum">Location</span><br />{i.location}</p>
                    <p><span className="overline-plum">Training</span><br />{i.training_type}</p>
                    <p><span className="overline-plum">Dates</span><br />{i.desired_dates}</p>
                    <p><span className="overline-plum">Students</span><br />{i.expected_students} · Rigging: {i.has_rigging}</p>
                  </div>
                  {i.message && <p className="mt-5 text-aviana-muted italic font-serif">"{i.message}"</p>}
                  <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-aviana-muted/70">
                    Received {new Date(i.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}

          {tab === "newsletter" && <NewsletterTab subscribers={data.newsletter} downloadWpExport={downloadWpExport} />}

          {tab === "offerings" && (
            <OfferingsTab offerings={data.offerings} reload={async () => {
              const { data: o } = await api.get("/offerings?include_past=true&include_cancelled=true&include_archived=true");
              setData((d) => ({ ...d, offerings: o }));
            }} />
          )}

          {tab === "photos" && <PhotosTab />}
          {tab === "courses" && <CoursesTab />}
          {tab === "journal" && <JournalTab />}
          {tab === "registrations" && <RegistrationsTab />}
          {tab === "event_registrations" && <EventRegistrationsTab />}
          {tab === "users" && <AdminUsersTab />}
          {tab === "contact" && <ContactMessagesTab />}
          {tab === "comp_access" && <CompAccessTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </div>
    </AdminShell>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="card-aviana p-7">
      <p className="overline-plum mb-3">{label}</p>
      <p className="font-display text-4xl text-aviana-ink">{value}</p>
      <p className="text-[11px] uppercase tracking-[0.2em] text-aviana-muted/70 mt-2">{sub}</p>
    </div>
  );
}

function Badge({ status }) {
  const map = {
    paid: "bg-aviana-sage/20 text-aviana-sage",
    pending: "bg-aviana-gold/20 text-aviana-goldDeep",
    new: "bg-aviana-plum/15 text-aviana-plum",
    contacted: "bg-aviana-sage/20 text-aviana-sage",
    archived: "bg-aviana-border text-aviana-muted",
  };
  return <span className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full ${map[status] || "bg-aviana-border text-aviana-muted"}`}>{status}</span>;
}

function Table({ headers, rows, testid }) {
  return (
    <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">{headers.map(h => <th key={h} className="overline-plum px-6 py-4 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="text-center py-12 text-aviana-muted">Nothing here yet.</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-t border-aviana-border/60 hover:bg-aviana-surface/50 transition-colors">
              {r.map((c, j) => <td key={j} className="px-6 py-4 text-aviana-inkSoft align-top">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Newsletter tab — source filter + country detection from IP ───────────────
// Friendly source labels so Holly sees "Book reader (Soaring With the Soul)"
// instead of the raw slug "soarwithme_book".
const SOURCE_LABELS = {
  soarwithme_book: "Book reader · Soaring With the Soul",
  footer: "Footer signup",
  signup: "Account signup",
  contact: "Contact form",
  backfill: "Imported (backfill)",
};
const sourceLabel = (s) => SOURCE_LABELS[s] || (s ? s.replace(/_/g, " ") : "Footer signup");

// "1st image", "2nd image", "3rd image" etc — used by the Journal repair panel
function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function flagFor(cc) {
  if (!cc || cc.length !== 2) return "";
  // Convert ISO-3166-1 alpha-2 to Unicode regional indicator flag emoji
  return cc.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

function NewsletterTab({ subscribers, downloadWpExport }) {
  const [sourceFilter, setSourceFilter] = useState("all");

  // Unique source values for the filter dropdown (with counts)
  const sourceCounts = useMemo(() => {
    const m = {};
    subscribers.forEach((s) => {
      const k = s.source || "footer";
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [subscribers]);

  const filtered = useMemo(() => {
    return sourceFilter === "all" ? subscribers : subscribers.filter((s) => (s.source || "footer") === sourceFilter);
  }, [subscribers, sourceFilter]);

  // Country breakdown for the filtered set
  const countryCounts = useMemo(() => {
    const m = {};
    filtered.forEach((s) => {
      const c = s.country_code || (s.country ? "??" : "");
      if (!c) return;
      m[c] = (m[c] || 0) + 1;
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  const exportFiltered = () => {
    const header = "name,email,source,country,country_code,region,city,joined\n";
    const rows = filtered.map((n) =>
      [n.name, n.email, n.source || "footer", n.country, n.country_code, n.region, n.city, n.created_at]
        .map((v) => `"${(v || "").toString().replace(/"/g, "''")}"`)
        .join(","),
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const slug = sourceFilter === "all" ? "all" : sourceFilter;
    a.href = url;
    a.download = `aviana-newsletter-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-newsletter">
      <div className="flex justify-between items-end mb-5 flex-wrap gap-4">
        <div>
          <p className="overline-plum">{filtered.length} of {subscribers.length} subscribers</p>
          <p className="text-sm text-aviana-muted mt-1">Filter by where they came from, then export. Beehiiv stays the source of truth — this view is for segmentation.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportFiltered} className="btn-gold !py-2.5 !px-6 !text-[11px]" data-testid="export-newsletter">
            <Download size={14} /> Export ({filtered.length}) as CSV
          </button>
          <button
            onClick={() => downloadWpExport("aviana_attendees.csv")}
            className="btn-secondary !py-2.5 !px-6 !text-[11px]"
            data-testid="export-wp-attendees"
          >
            <Download size={14} /> WP Attendees & Orders (303)
          </button>
        </div>
      </div>

      {/* Source filter pills */}
      <div className="flex gap-2 mb-5 flex-wrap" data-testid="newsletter-source-filter">
        <button
          onClick={() => setSourceFilter("all")}
          className={`px-4 py-2 rounded-full text-[11px] tracking-[0.14em] uppercase border transition-colors ${sourceFilter === "all" ? "bg-aviana-ink text-aviana-cream border-aviana-ink" : "border-aviana-border text-aviana-muted hover:text-aviana-ink"}`}
          data-testid="filter-all"
        >
          All ({subscribers.length})
        </button>
        {Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]).map(([src, count]) => (
          <button
            key={src}
            onClick={() => setSourceFilter(src)}
            className={`px-4 py-2 rounded-full text-[11px] tracking-[0.14em] uppercase border transition-colors ${sourceFilter === src ? "bg-aviana-plum text-aviana-cream border-aviana-plum" : "border-aviana-border text-aviana-muted hover:text-aviana-ink"}`}
            data-testid={`filter-${src}`}
          >
            {sourceLabel(src)} ({count})
          </button>
        ))}
      </div>

      {/* Country breakdown — only shown if we have geo data on at least one sub */}
      {countryCounts.length > 0 && (
        <div className="bg-aviana-cream border border-aviana-border rounded-2xl p-5 mb-5" data-testid="country-breakdown">
          <p className="overline-plum mb-3">Where they're reading from</p>
          <div className="flex flex-wrap gap-2.5">
            {countryCounts.map(([cc, n]) => (
              <span
                key={cc}
                className="inline-flex items-center gap-2 bg-aviana-paper border border-aviana-border rounded-full px-3.5 py-1.5 text-[12px]"
                data-testid={`country-${cc}`}
              >
                <span className="text-base">{flagFor(cc)}</span>
                <span className="text-aviana-ink">{cc}</span>
                <span className="text-aviana-muted">· {n}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <Table
        headers={["Name", "Email", "Source", "Country", "Joined"]}
        rows={filtered.map((n) => [
          n.name || "—",
          n.email,
          sourceLabel(n.source),
          n.country ? `${flagFor(n.country_code)} ${n.country}${n.region ? ` · ${n.region}` : ""}` : "—",
          new Date(n.created_at).toLocaleDateString(),
        ])}
      />
    </div>
  );
}

// ─── Offerings tab ────────────────────────────────────────────────────────────
function OfferingsTab({ offerings, reload }) {
  const [editing, setEditing] = useState(null); // offering object or null
  const [creating, setCreating] = useState(false);
  const [viewingAttendees, setViewingAttendees] = useState(null); // offering object or null
  const [emailing, setEmailing] = useState(null); // offering whose confirmation emails are being previewed
  return (
    <div data-testid="admin-offerings">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <p className="text-sm text-aviana-muted">Click <strong>Attendees</strong> to see who's registered, <strong>Edit</strong> to change details, or <strong>Delete</strong> to cancel an event.</p>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="btn-gold !py-2.5 !px-5 !text-[11px]"
          data-testid="new-offering-btn"
        >
          + New offering
        </button>
      </div>
      <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              {["Photo", "Title", "Type", "Date", "Price", "Spots", ""].map((h) => (
                <th key={h} className="overline-plum px-5 py-4 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {offerings.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-aviana-muted">No offerings yet.</td></tr>
            ) : offerings.map((o) => (
              <tr key={o.id} className={`border-t border-aviana-border/60 ${o.archived ? "opacity-60" : ""}`} data-testid={`offering-row-${o.id}`}>
                <td className="px-5 py-3">
                  <img src={o.image_url} alt="" className="w-16 h-12 rounded-lg object-cover" />
                </td>
                <td className="px-5 py-3 text-aviana-inkSoft">
                  {o.title}
                  {o.archived && (
                    <span className="ml-2 inline-block text-[9px] tracking-[0.2em] uppercase bg-aviana-border/60 text-aviana-muted px-2 py-0.5 rounded-full">Archived</span>
                  )}
                  {o.cancelled && (
                    <span className="ml-2 inline-block text-[9px] tracking-[0.2em] uppercase bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Cancelled</span>
                  )}
                </td>
                <td className="px-5 py-3 text-aviana-muted capitalize">{o.type}</td>
                <td className="px-5 py-3 text-aviana-muted">{o.start_date || "—"}</td>
                <td className="px-5 py-3 text-aviana-muted">${o.price_usd}{o.deposit_usd ? ` / $${o.deposit_usd} dep` : ""}</td>
                <td className="px-5 py-3 text-aviana-muted">{o.spots_left < 9999 ? `${o.spots_left}/${o.capacity}` : "open"}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setViewingAttendees(o)} className="btn-secondary !py-1.5 !px-3 !text-[10px] mr-2" data-testid={`attendees-${o.id}`}>Attendees</button>
                  <button onClick={() => setEditing(o)} className="btn-secondary !py-1.5 !px-3 !text-[10px] mr-2" data-testid={`edit-offering-${o.id}`}>Edit</button>
                  <button onClick={() => setEmailing(o)} className="btn-secondary !py-1.5 !px-3 !text-[10px] mr-2" data-testid={`email-preview-${o.id}`} title="Preview & edit the confirmation emails students receive">Emails</button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Duplicate "${o.title}"?\n\nA copy will be created (hidden from the public site) so you can update the dates, hero photo, and description for the next occurrence.`)) return;
                      try {
                        const { data } = await api.post(`/admin/offerings/${o.id}/duplicate`);
                        toast.success("Duplicated — opening editor…");
                        await reload();
                        setEditing(data);
                      } catch (e) {
                        toast.error(formatApiError(e) || "Could not duplicate offering");
                      }
                    }}
                    className="btn-secondary !py-1.5 !px-3 !text-[10px] mr-2 !text-aviana-goldDeep hover:!bg-aviana-gold/10"
                    data-testid={`duplicate-offering-${o.id}`}
                    title="Clone this offering to reuse for next month · resets dates and hides until you're ready"
                  >
                    Duplicate
                  </button>
                  <button
                    onClick={async () => {
                      const url = `${window.location.origin}/offering/${o.slug}`;
                      const dateLine = o.schedule_summary || o.start_date || "";
                      const priceLine = o.price_usd ? `$${o.price_usd}` : "";
                      const caption = [
                        `✨ ${o.title}`,
                        dateLine && `📅 ${dateLine}`,
                        o.location && `📍 ${o.location}`,
                        priceLine && `💰 ${priceLine}`,
                        "",
                        (o.subtitle || o.description || "").split(/\.\s+/).slice(0, 2).join(". ").slice(0, 240) + ".",
                        "",
                        `Reserve your spot: ${url}`,
                        "",
                        o.type === "training"
                          ? "#aerialyoga #yogateachertraining #aerialyogateacher #yacep"
                          : o.type === "retreat"
                          ? "#yogaretreat #sacredfeminine #womensretreat #sisterhood"
                          : "#sisterhood #newmoon #womencircle #aerialyoga #avianayoga",
                      ].filter(Boolean).join("\n");
                      try {
                        await navigator.clipboard.writeText(caption);
                        toast.success("Caption + link copied. Paste it into Instagram, Facebook, or a Facebook Event.");
                      } catch {
                        window.prompt("Copy this caption for Instagram / Facebook:", caption);
                      }
                    }}
                    className="btn-secondary !py-1.5 !px-3 !text-[10px] mr-2 !text-aviana-plum hover:!bg-aviana-plum/10"
                    data-testid={`share-caption-${o.id}`}
                    title="Copy a ready-to-paste Instagram / Facebook caption for this offering"
                  >
                    Copy caption
                  </button>
                  <button
                    onClick={async () => {
                      const nextState = !o.archived;
                      const verb = nextState ? "archive" : "un-archive";
                      const desc = nextState
                        ? `Archive "${o.title}"?\n\nIt will be hidden from all public listings (nav, retreats page, featured, home).\nThe direct URL will still work so you can preview it and un-archive later.`
                        : `Un-archive "${o.title}"?\n\nIt will be visible again on the public site.`;
                      if (!window.confirm(desc)) return;
                      try {
                        await api.patch(`/admin/offerings/${o.id}`, { archived: nextState });
                        toast.success(nextState ? "Offering archived" : "Offering restored");
                        await reload();
                      } catch (e) {
                        toast.error(formatApiError(e) || `Could not ${verb} offering`);
                      }
                    }}
                    className={`btn-secondary !py-1.5 !px-3 !text-[10px] mr-2 ${o.archived ? "!text-aviana-goldDeep hover:!bg-aviana-gold/10" : "!text-aviana-plum hover:!bg-aviana-plum/10"}`}
                    data-testid={`archive-offering-${o.id}`}
                    title={o.archived ? "Bring this offering back to the public site" : "Hide this offering from the public site (data preserved)"}
                  >
                    {o.archived ? "Un-archive" : "Archive"}
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete "${o.title}"?\n\nThis will refuse if attendees exist. To cancel an event with attendees, contact dev.`)) return;
                      try {
                        await api.delete(`/admin/offerings/${o.id}`);
                        toast.success("Offering deleted");
                        await reload();
                      } catch (e) {
                        const msg = formatApiError(e);
                        if (msg.toLowerCase().includes("cannot delete")) {
                          if (window.confirm(`${msg}\n\nCANCEL EVENT and delete all attendees + booking history?`)) {
                            await api.delete(`/admin/offerings/${o.id}?force=true`);
                            toast.success("Event cancelled · attendees purged");
                            await reload();
                          }
                        } else {
                          toast.error(msg);
                        }
                      }
                    }}
                    className="btn-secondary !py-1.5 !px-3 !text-[10px] !text-red-700 hover:!bg-red-50"
                    data-testid={`delete-offering-${o.id}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <OfferingEditor
          offering={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { await reload(); setEditing(null); }}
        />
      )}
      {creating && (
        <NewOfferingDialog
          onClose={() => setCreating(false)}
          onCreated={async () => { await reload(); setCreating(false); }}
        />
      )}
      {viewingAttendees && (
        <AttendeesDialog
          offering={viewingAttendees}
          onClose={() => setViewingAttendees(null)}
        />
      )}
      {emailing && (
        <EmailPreviewModal
          offering={emailing}
          onClose={() => setEmailing(null)}
          onSaved={async () => { await reload(); setEmailing(null); }}
        />
      )}
    </div>
  );
}

// ─── Attendees dialog — name/phone/email summary + expandable full details ────
// Attendee `source` → cancel endpoint source (training_registrations report kind or "registration").
const ATTENDEE_SOURCE = { booking: "booking", event: "event", registration: "training", retreat: "training", training: "training" };
function AttendeesDialog({ offering, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const loadAttendees = async () => {
    try {
      const { data } = await api.get(`/admin/offerings/${offering.id}/attendees`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      onClose();
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAttendees(); }, [offering.id]);

  return (
    <div className="fixed inset-0 z-50 bg-aviana-plumDeep/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-8" data-testid="attendees-dialog">
      <div className="bg-aviana-paper rounded-[2rem] shadow-2xl w-full max-w-4xl my-8 relative">
        <button onClick={onClose} className="absolute top-5 right-5 text-aviana-muted hover:text-aviana-ink p-2" data-testid="close-attendees"><X size={22} /></button>
        <div className="p-7 md:p-10">
          <p className="overline-plum mb-2">Attendees</p>
          <h3 className="font-display text-3xl text-aviana-ink mb-1">{offering.title}</h3>
          <p className="text-sm text-aviana-muted mb-7">{offering.start_date || "Self-paced"} · {data ? `${data.count} attendee${data.count === 1 ? "" : "s"}` : "—"}</p>

          {loading && <p className="text-aviana-muted py-10 text-center">Loading…</p>}

          {!loading && data && data.attendees.length === 0 && (
            <div className="py-14 text-center text-aviana-muted">
              <p className="text-[15px]">No one has registered yet.</p>
            </div>
          )}

          {!loading && data && data.attendees.length > 0 && (
            <div className="border border-aviana-border rounded-2xl overflow-hidden bg-aviana-cream">
              <table className="w-full text-sm">
                <thead className="bg-aviana-cream2/50">
                  <tr className="text-left">
                    {["Name", "Email", "Phone", "Paid", "Balance", "Status", ""].map((h) => (
                      <th key={h} className="overline-plum px-4 py-3 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.attendees.map((a) => (
                    <React.Fragment key={a.id}>
                      <tr className="border-t border-aviana-border/60" data-testid={`attendee-row-${a.id}`}>
                        <td className="px-4 py-3 text-aviana-ink font-medium">{a.name}</td>
                        <td className="px-4 py-3 text-aviana-inkSoft"><a href={`mailto:${a.email}`} className="hover:text-aviana-plum">{a.email}</a></td>
                        <td className="px-4 py-3 text-aviana-inkSoft">{a.phone}</td>
                        <td className="px-4 py-3 text-aviana-inkSoft">${a.amount_paid_usd}</td>
                        <td className="px-4 py-3 text-aviana-muted">{a.balance_due_usd > 0 ? `$${a.balance_due_usd}` : "—"}</td>
                        <td className="px-4 py-3"><span className="text-[10px] tracking-[0.18em] uppercase text-aviana-plum">{a.status}</span></td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {a.status !== "cancelled" && !(a.source === "booking" && offering.type === "course") && (
                            <span className="mr-3 inline-block">
                              <CancelRegistrationButton source={ATTENDEE_SOURCE[a.source] || "training"} id={a.id} onDone={loadAttendees} label="Cancel" />
                            </span>
                          )}
                          {a.source === "registration" && (
                            <button onClick={() => setExpanded(expanded === a.id ? null : a.id)} className="text-[10px] tracking-[0.18em] uppercase text-aviana-goldDeep hover:text-aviana-plum" data-testid={`expand-${a.id}`}>
                              {expanded === a.id ? "Hide" : "Full"}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expanded === a.id && (
                        <tr className="bg-aviana-cream2/30 border-t border-aviana-border/60" data-testid={`attendee-detail-${a.id}`}>
                          <td colSpan={7} className="px-5 py-5">
                            <dl className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-[13px]">
                              {Object.entries(a.full).filter(([k]) => !["_id"].includes(k)).map(([k, v]) => (
                                <div key={k} className="flex gap-3 border-b border-aviana-border/30 py-1.5">
                                  <dt className="text-aviana-muted capitalize w-44 shrink-0">{k.replace(/_/g, " ")}</dt>
                                  <dd className="text-aviana-ink break-words">
                                    {v == null || v === "" ? <span className="text-aviana-muted/50">—</span> : (typeof v === "object" ? <code className="text-[11px]">{JSON.stringify(v)}</code> : String(v))}
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OfferingEditor({ offering, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: offering.title || "",
    slug: offering.pending_setup ? "" : (offering.slug || ""),
    subtitle: offering.subtitle || "",
    location: offering.location || "",
    start_date: offering.start_date || "",
    end_date: offering.end_date || "",
    start_time: offering.start_time || "",
    end_time: offering.end_time || "",
    schedule_summary: offering.schedule_summary || "",
    image_url: offering.image_url || "",
    price_usd: offering.price_usd || 0,
    deposit_usd: offering.deposit_usd || 0,
    spots_left: offering.spots_left ?? 0,
    capacity: offering.capacity ?? 0,
    balance_due_days_before: offering.balance_due_days_before ?? 7,
    cancellation_policy: offering.cancellation_policy || "",
    description: offering.description || "",
    highlights: (offering.highlights || []).join("\n"),
    important_details: (offering.important_details || []).join("\n"),
    special_tuition_usd: offering.special_tuition_usd || 0,
    special_tuition_label: offering.special_tuition_label || "",
    registration_form: offering.registration_form || "",
    bliss_tent: !!offering.bliss_tent,
  });
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [slugTouched, setSlugTouched] = useState(!!offering.slug && !offering.pending_setup);

  // Simple slugify — matches the backend's normalizer so what the user sees
  // is what will be saved.
  const slugify = (s) =>
    (s || "").toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");

  const isSetupMode = !!offering.pending_setup;

  useEffect(() => {
    api.get("/admin/photos").then(({ data }) => setPhotos(data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        title: form.title,
        slug: form.slug.trim() || null,
        subtitle: form.subtitle,
        location: form.location.trim() || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        registration_form: form.registration_form || null,
        schedule_summary: form.schedule_summary.trim() || null,
        image_url: form.image_url,
        price_usd: Number(form.price_usd) || 0,
        deposit_usd: Number(form.deposit_usd) || null,
        spots_left: Number(form.spots_left),
        capacity: Number(form.capacity),
        balance_due_days_before: Number(form.balance_due_days_before),
        cancellation_policy: form.cancellation_policy.trim() || null,
        description: form.description,
        highlights: form.highlights.split("\n").map((s) => s.trim()).filter(Boolean),
        important_details: form.important_details.split("\n").map((s) => s.trim()).filter(Boolean),
        special_tuition_usd: Number(form.special_tuition_usd) || null,
        special_tuition_label: form.special_tuition_label.trim() || null,
        bliss_tent: !!form.bliss_tent,
      };
      // Once the user has given the duplicated offering a real title + slug,
      // clear the pending_setup flag so the modal stops opening in setup mode.
      if (isSetupMode && form.title.trim() && form.slug.trim()) {
        body.pending_setup = false;
      }
      await api.patch(`/admin/offerings/${offering.id}`, body);
      toast.success("Offering updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const heroPreview = form.image_url?.startsWith("http") ? form.image_url : `${BACKEND_URL}${form.image_url}`;

  return (
    <div className="fixed inset-0 bg-aviana-ink/70 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose} data-testid="offering-editor">
      <div className="bg-aviana-paper rounded-2xl w-full max-w-3xl my-10" onClick={(e) => e.stopPropagation()}>
        <header className="px-7 py-5 border-b border-aviana-border bg-aviana-cream rounded-t-2xl flex items-center justify-between">
          <div>
            <p className="overline-plum mb-1">{isSetupMode ? "Finish setup" : "Edit offering"}</p>
            <h3 className="font-display text-2xl text-aviana-ink">{offering.title || "New duplicate — untitled"}</h3>
          </div>
          <button onClick={onClose} className="text-aviana-muted hover:text-aviana-ink text-2xl leading-none">×</button>
        </header>
        {isSetupMode && (
          <div className="px-7 pt-5" data-testid="setup-mode-banner">
            <div className="rounded-xl border border-aviana-gold/40 bg-aviana-gold/8 px-5 py-4 text-[13.5px] text-aviana-inkSoft leading-[1.65]">
              <p><strong className="text-aviana-ink">Just duplicated — 3 quick tweaks and you're done.</strong> Give the new occurrence a title (the slug will follow along), set the date, and hit save. The clone stays hidden from the public site until you toggle off <em>Archived</em>.</p>
            </div>
          </div>
        )}
        <div className="p-7 space-y-6">
          {/* Hero photo */}
          <div>
            <label className="overline-plum mb-2 block">Hero photo</label>
            <div className="flex gap-4 items-start">
              <div className="w-48 h-32 rounded-xl overflow-hidden bg-aviana-cream border border-aviana-border shrink-0">
                {form.image_url ? <img src={heroPreview} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-aviana-muted text-xs">No photo</div>}
              </div>
              <div className="flex-1">
                <button onClick={() => setShowPicker(true)} className="btn-secondary !py-2 !px-4 !text-[11px]" data-testid="pick-offering-photo">
                  {form.image_url ? "Change photo" : "Pick photo"}
                </button>
                <p className="text-xs text-aviana-muted mt-2">Pick any photo from your library. Upload new ones in the Photos tab first.</p>
              </div>
            </div>
            {showPicker && (
              <div className="mt-4 max-h-72 overflow-y-auto bg-aviana-cream rounded-xl border border-aviana-border p-3 grid grid-cols-4 md:grid-cols-6 gap-2" data-testid="offering-photo-grid">
                {photos.map((p) => {
                  const url = `${BACKEND_URL}${p.url}`;
                  const selected = form.image_url === url;
                  return (
                    <button
                      key={p.id}
                      onClick={() => { setForm((f) => ({ ...f, image_url: url })); setShowPicker(false); }}
                      className={`relative aspect-square rounded-lg overflow-hidden ring-2 transition-all ${selected ? "ring-aviana-gold" : "ring-transparent hover:ring-aviana-plum/40"}`}
                      data-testid={`photo-option-${p.id}`}
                    >
                      <img src={url} alt={p.filename} className="w-full h-full object-cover" />
                    </button>
                  );
                })}
                {photos.length === 0 && <p className="col-span-full text-center text-aviana-muted py-6 text-xs">No photos in library yet.</p>}
              </div>
            )}
          </div>

          <Field
            label="Title"
            value={form.title}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                title: v,
                slug: slugTouched ? f.slug : slugify(v),
              }))
            }
          />
          <Field
            label="URL slug"
            value={form.slug}
            onChange={(v) => {
              setSlugTouched(true);
              setForm((f) => ({ ...f, slug: v }));
            }}
            hint={isSetupMode
              ? "This will be the tail of the public URL. It's auto-generated from the Title — feel free to override."
              : "The tail of the public link, e.g. avianayoga.com/offering/full-moon-sister-circle-oct. Lower-case letters, numbers, and hyphens only — save will auto-clean it. Existing registrations will follow the rename automatically."
            }
            testid="edit-slug"
          />
          <Field label="Subtitle" value={form.subtitle} onChange={(v) => setForm((f) => ({ ...f, subtitle: v }))} />
          <Field
            label="Location"
            value={form.location}
            onChange={(v) => setForm((f) => ({ ...f, location: v }))}
            hint="Where the event happens — used on the offering page, in confirmation emails, and for local SEO. e.g. 'Sugar Grove, IL', 'The Pearl · Naperville, IL', or 'Online (Zoom)'."
            testid="edit-location"
          />

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Start date"
              type="date"
              value={form.start_date}
              onChange={(v) => setForm((f) => ({ ...f, start_date: v }))}
              hint="Used for sort order + the date column. Leave blank for self-paced courses."
              testid="edit-start-date"
            />
            <Field
              label="End date"
              type="date"
              value={form.end_date}
              onChange={(v) => setForm((f) => ({ ...f, end_date: v }))}
              hint="Optional — for multi-day retreats/trainings only."
              testid="edit-end-date"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Start time"
              type="time"
              value={form.start_time}
              onChange={(v) => setForm((f) => ({ ...f, start_time: v }))}
              hint="Shown on the offering page + listing cards. e.g. 1:00 PM"
              testid="edit-start-time"
            />
            <Field
              label="End time"
              type="time"
              value={form.end_time}
              onChange={(v) => setForm((f) => ({ ...f, end_time: v }))}
              hint="Shown as a range with the start time — e.g. '1–7 pm'."
              testid="edit-end-time"
            />
          </div>

          <Field
            label="Schedule / times"
            value={form.schedule_summary}
            onChange={(v) => setForm((f) => ({ ...f, schedule_summary: v }))}
            hint="Human-readable times, shown on the offering page and registration email. e.g. 'Saturday, Nov 15 · 6–8pm CT' or 'Fri 5–8pm · Sat–Sun 9am–6pm'"
          />

          <label className="block">
            <span className="overline-plum mb-2 block">Registration form</span>
            <select
              value={form.registration_form}
              onChange={(e) => setForm((f) => ({ ...f, registration_form: e.target.value }))}
              className="w-full bg-aviana-cream border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30"
              data-testid="edit-registration-form"
            >
              <option value="">Auto — infer from offering type</option>
              <option value="destination_retreat">Destination Retreat (full intake — dob, address, emergency contact, health, hat size, riding experience, waivers)</option>
              <option value="day_retreat">Day Retreat (like Destination minus hat/riding — good for day workshops that include a meal)</option>
              <option value="teacher_training">Aerial Yoga Teacher Training (yoga/aerial experience, time-commitment, terms, waivers, book add-on)</option>
              <option value="workshop_class">Workshop / Class (short — name, email, phone, emergency contact, waivers)</option>
            </select>
            <span className="text-[11px] text-aviana-muted mt-1.5 block">
              Choose which intake form students fill out before Stripe. Leave "Auto" to let the system infer from offering type.
            </span>
          </label>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Price (USD)" type="number" value={form.price_usd} onChange={(v) => setForm((f) => ({ ...f, price_usd: v }))} />
            <Field label="Deposit (USD)" type="number" value={form.deposit_usd} onChange={(v) => setForm((f) => ({ ...f, deposit_usd: v }))} />
            <Field label="Capacity" type="number" value={form.capacity} onChange={(v) => setForm((f) => ({ ...f, capacity: v }))} />
            <Field label="Spots left" type="number" value={form.spots_left} onChange={(v) => setForm((f) => ({ ...f, spots_left: v }))} />
          </div>

          <Field
            label="Balance due (days before start)"
            type="number"
            value={form.balance_due_days_before}
            onChange={(v) => setForm((f) => ({ ...f, balance_due_days_before: v }))}
            hint="e.g. 7 = balance due 1 week before training starts"
          />

          {/* Special tuition (e.g. hosting-studio employees). When set, registrants
              who answer "Yes" to 'affiliated with hosting studio' get this rate. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-aviana-gold/5 border border-aviana-gold/25 rounded-xl p-5">
            <div className="md:col-span-3">
              <p className="overline-plum text-[10px] mb-1">Special tuition (optional)</p>
              <p className="text-aviana-muted text-[11px]">If a host studio offers a discounted rate for their own instructors, set the price + label here. Leave blank for no special rate.</p>
            </div>
            <Field label="Special tuition (USD)" type="number" value={form.special_tuition_usd} onChange={(v) => setForm((f) => ({ ...f, special_tuition_usd: v }))} />
            <div className="md:col-span-2">
              <Field label="Special tuition label" value={form.special_tuition_label} onChange={(v) => setForm((f) => ({ ...f, special_tuition_label: v }))} hint="e.g. 'Special rate for current Pearl instructors'" />
            </div>
          </div>

          <Field
            label="Description (markdown — use ## for section headings, - for bullets, **bold**)"
            value={form.description}
            onChange={(v) => setForm((f) => ({ ...f, description: v }))}
            multiline
            rows={10}
          />

          <Field
            label="Highlights (one per line — what's included)"
            value={form.highlights}
            onChange={(v) => setForm((f) => ({ ...f, highlights: v }))}
            multiline
            rows={6}
            hint="Lines containing 'amazon' or 'soaring with the soul' automatically get the affiliate link."
          />

          <Field
            label="Important details (one per line — what to bring · what to wear · parking · arrival)"
            value={form.important_details}
            onChange={(v) => setForm((f) => ({ ...f, important_details: v }))}
            multiline
            rows={5}
            hint="Practical logistics shown as a separate card on the offering page. Great for sister circles, workshops, and day events."
          />

          <label className="flex items-start gap-3 bg-aviana-cream/70 border border-aviana-border rounded-xl px-4 py-3 cursor-pointer" data-testid="bliss-tent-toggle-label">
            <input
              type="checkbox"
              checked={form.bliss_tent}
              onChange={(e) => setForm((f) => ({ ...f, bliss_tent: e.target.checked }))}
              className="accent-aviana-plum mt-1"
              data-testid="bliss-tent-toggle"
            />
            <span>
              <span className="block text-sm text-aviana-ink font-medium">Held at the Bliss Tent (send parking instructions)</span>
              <span className="block text-xs text-aviana-muted mt-0.5">Confirmation emails will include the Bliss Tent address and private-residence parking instructions.</span>
            </span>
          </label>

          <Field
            label="Cancellation policy (leave blank to use the default)"
            value={form.cancellation_policy}
            onChange={(v) => setForm((f) => ({ ...f, cancellation_policy: v }))}
            multiline
            rows={4}
          />
        </div>
        <footer className="px-7 py-5 border-t border-aviana-border bg-aviana-cream flex items-center justify-end gap-3 rounded-b-2xl">
          <button onClick={onClose} className="btn-secondary !py-2 !px-5 !text-[11px]">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-gold !py-2.5 !px-6 !text-[11px]" data-testid="save-offering">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", multiline = false, rows = 3, hint, testid }) {
  return (
    <label className="block">
      <span className="overline-plum mb-2 block">{label}</span>
      {multiline ? (
        <textarea
          rows={rows}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-aviana-cream border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink leading-[1.55] focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30"
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-aviana-cream border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30"
        />
      )}
      {hint && <span className="text-[11px] text-aviana-muted mt-1.5 block">{hint}</span>}
    </label>
  );
}

function RegistrationsTab() {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(null);
  const [logPaymentFor, setLogPaymentFor] = useState(null);
  const [paymentDraft, setPaymentDraft] = useState({ amount_usd: "", method: "venmo", note: "", send_receipt_email: true, include_stripe_remainder_link: true });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/trainings/registrations");
      setRegs(data);
    } catch { toast.error("Could not load registrations"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/admin/trainings/registrations/${id}`, { status });
      toast.success(`Marked as ${status.replaceAll("_", " ")}`);
      await load();
    } catch (_e) { toast.error("Could not update status"); }
    finally { setBusy(null); }
  };

  const setFinalTuition = async (reg) => {
    const raw = window.prompt(
      `Set the final tuition for ${reg.full_name}'s ${reg.offering_title} registration.\n\n` +
      `They've paid $${reg.amount_paid_usd ?? reg.amount_today_usd ?? 0} so far.\n` +
      `Their balance due will auto-calculate.\n\n` +
      `Enter the total tuition (in USD, whole dollars):`,
      String(reg.offering_price_usd || "")
    );
    if (raw == null) return;
    const amount = parseInt(raw.replace(/[^0-9]/g, ""), 10);
    if (!amount || amount < 0) { toast.error("Please enter a valid amount"); return; }
    setBusy(reg.id);
    try {
      await api.patch(`/admin/trainings/registrations/${reg.id}`, { final_tuition_usd: amount });
      toast.success(`Tuition set to $${amount.toLocaleString()}`);
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not set tuition"); }
    finally { setBusy(null); }
  };

  const sendBalanceInvoice = async (reg, opts = {}) => {
    const isPreview = !!opts.preview;
    const defaultAmt = reg.balance_due_usd || 0;
    if (defaultAmt <= 0 && !isPreview) {
      toast.error("No balance due — nothing to invoice");
      return;
    }
    // For preview mode when balance is 0, use a sample amount so we can render
    // the email. Uses the offering price as a helpful default.
    const promptDefault = defaultAmt > 0 ? defaultAmt : (reg.offering_price_usd || 1100);
    const promptTitle = isPreview
      ? `PREVIEW MODE — Send a sample invoice email to YOUR admin address (you'll receive it, NOT the student).\n\nSample balance amount to render:`
      : `Send a balance-due email to ${reg.email}.\n\nFull balance: $${defaultAmt.toLocaleString()}.\nEnter a partial amount for installment plans, or leave as-is for the full balance:`;
    const raw = window.prompt(promptTitle, String(promptDefault));
    if (raw == null) return;
    const amount = parseInt(raw.replace(/[^0-9]/g, ""), 10);
    if (!amount || amount <= 0) { toast.error("Please enter a valid amount"); return; }
    const due = window.prompt(
      `Due-by date (optional).\n\n` +
      `You can enter either:\n` +
      `  • YYYY-MM-DD  (e.g., 2026-08-15 → renders as "August 15, 2026")\n` +
      `  • Freeform label (e.g., "Aug 15" or "before the training starts")\n\n` +
      `Leave blank to omit the due date entirely.`,
      ""
    );
    if (due == null) return;
    const note = window.prompt(
      `Optional note to include in the email (leave blank for none):\n\n` +
      `Example: "This is your first of 3 monthly installments of $367 for the Itasca training."`,
      ""
    );
    if (note == null) return;
    setBusy(reg.id);
    try {
      const { data } = await api.post(`/admin/trainings/registrations/${reg.id}/send-balance-invoice`, {
        override_amount_usd: amount,
        note,
        due_by: due.trim() || null,
        preview: isPreview,
      });
      toast.success(
        isPreview
          ? `Sample email sent to YOU (${data.sent_to}) — check your inbox`
          : `Balance email sent to ${data.sent_to}`
      );
      if (!isPreview) await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not send email"); }
    finally { setBusy(null); }
  };

  const submitPayment = async (regId) => {
    const amt = parseFloat(paymentDraft.amount_usd);
    if (!amt || amt <= 0) { toast.error("Enter an amount greater than 0"); return; }
    setBusy(regId);
    try {
      await api.post(`/admin/registrations/${regId}/log-payment`, {
        amount_usd: amt,
        method: paymentDraft.method,
        note: paymentDraft.note,
        send_receipt_email: paymentDraft.send_receipt_email,
        include_stripe_remainder_link: paymentDraft.include_stripe_remainder_link,
      });
      toast.success(`$${amt} payment logged · receipt sent`);
      setLogPaymentFor(null);
      setPaymentDraft({ amount_usd: "", method: "venmo", note: "", send_receipt_email: true, include_stripe_remainder_link: true });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not log payment");
    } finally { setBusy(null); }
  };

  if (loading) return <div className="text-aviana-muted">Loading registrations…</div>;
  if (regs.length === 0) {
    return (
      <div className="card-aviana p-12 text-center text-aviana-muted">
        <ClipboardList size={32} className="mx-auto mb-4 text-aviana-gold/60" />
        <p>No training registrations yet. They'll show up here the moment someone completes the intake form.</p>
      </div>
    );
  }

  const statusBadge = (s) => {
    const map = {
      pending_payment:           { l: "Pending payment", c: "bg-aviana-gold/20 text-aviana-goldDeep" },
      awaiting_manual_payment:   { l: "Awaiting Venmo/Zelle", c: "bg-aviana-plum/15 text-aviana-plum" },
      deposit_paid:              { l: "Deposit paid", c: "bg-aviana-sage/20 text-aviana-sage" },
      paid_in_full:              { l: "Paid in full", c: "bg-aviana-sage/30 text-aviana-sage" },
      cancelled:                 { l: "Cancelled", c: "bg-aviana-border text-aviana-muted line-through" },
    };
    const m = map[s] || { l: s, c: "bg-aviana-border text-aviana-muted" };
    return <span className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full ${m.c}`}>{m.l}</span>;
  };

  return (
    <div data-testid="admin-registrations">
      <p className="text-sm text-aviana-muted mb-6">Every long-form retreat & training registration appears here. Click a row to see the full intake — and to <strong>log partial payments</strong> (Venmo / Zelle / cash) for installment-based retreats. Logged payments auto-send the attendee a receipt with the remaining balance and a one-click Stripe link.</p>
      <div className="space-y-3">
        {regs.map((r) => {
          const isOpen = expanded === r.id;
          const isRetreat = r.kind === "retreat" || r.offering_type === "retreat";
          const paymentsLog = r.payments_log || [];
          const paidTotal = r.amount_paid_usd ?? (paymentsLog.reduce((s, p) => s + (p.amount_usd || 0), 0) || (["deposit_paid","paid_in_full"].includes(r.status) ? r.amount_today_usd : 0));
          // Deposit-only tuition still pending finalization: don't mislead with "Paid in full".
          const tuitionPending = !!r.deposit_only && !r.tuition_finalized;
          const balanceLabel = tuitionPending
            ? "Balance TBD (set final tuition)"
            : (r.balance_due_usd ? `$${r.balance_due_usd.toLocaleString()}` : "Paid in full");
          return (
            <div key={r.id} className="bg-aviana-cream rounded-2xl border border-aviana-border overflow-hidden" data-testid={`reg-${r.id}`}>
              <button onClick={() => setExpanded(isOpen ? null : r.id)} className="w-full p-5 flex items-center gap-5 text-left hover:bg-aviana-paper/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <p className="font-display text-lg text-aviana-ink truncate">{r.full_name}</p>
                    {statusBadge(r.status)}
                    {isRetreat && <span className="text-[9px] tracking-[0.2em] uppercase bg-aviana-plum/10 text-aviana-plum px-2 py-0.5 rounded">retreat</span>}
                    {r.pricing_tier === "sisterhood" && <span className="text-[9px] tracking-[0.2em] uppercase bg-aviana-gold/15 text-aviana-goldDeep px-2 py-0.5 rounded">sisterhood</span>}
                    {r.payment_method && <span className="text-[10px] tracking-[0.2em] uppercase text-aviana-muted">via {r.payment_method}</span>}
                    {r.add_book && <span className="text-[10px] tracking-[0.2em] uppercase text-aviana-gold">+ book</span>}
                  </div>
                  <p className="mt-1 text-[12px] text-aviana-muted truncate">
                    {r.offering_title} · paid ${paidTotal?.toLocaleString?.() || 0} of {tuitionPending ? "tuition TBD" : `$${r.offering_price_usd?.toLocaleString()}`} · balance {tuitionPending ? "TBD" : `$${r.balance_due_usd?.toLocaleString?.() || 0}`} · {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <p className="text-[11px] text-aviana-muted">{r.email}</p>
              </button>
              {isOpen && (
                <div className="px-6 pb-6 pt-2 border-t border-aviana-border/60 bg-white" data-testid={`reg-details-${r.id}`}>
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 mt-4 text-[13px]">
                    <Detail label="Phone" value={r.phone} />
                    <Detail label="Date of birth" value={r.dob} />
                    <Detail label="Mailing address" value={r.address} />
                    <Detail label="Emergency contact" value={`${r.emergency_contact_name} (${r.emergency_contact_relationship}) · ${r.emergency_contact_phone}`} />
                    <Detail label="Condition of health" value={r.health_condition} />
                    {isRetreat ? (
                      <>
                        <Detail label="Hat size" value={r.hat_size || "—"} />
                        <Detail label="Riding experience" value={r.horseback_experience || "—"} />
                        <Detail label="Riding notes" value={r.horseback_experience_notes || "—"} wide />
                        <Detail label="Dietary" value={r.dietary_restrictions || "—"} wide />
                        <Detail label="Why this retreat" value={r.why_this_retreat || "—"} wide />
                        <Detail label="Liability waiver" value={r.liability_waiver_agreed ? "Yes" : "No"} />
                        <Detail label="Photo release" value={r.photo_release_agreed ? "Yes" : "No"} />
                        {r.pricing_tier === "sisterhood" && <Detail label="Registering with sister" value={r.sisterhood_partner_name || "—"} />}
                      </>
                    ) : (
                      <>
                        <Detail label="Profession" value={r.profession || "—"} />
                        <Detail label="Yoga experience" value={r.yoga_experience || "—"} wide />
                        <Detail label="Why this training" value={r.why_this_training || "—"} wide />
                        <Detail label="Time commitment" value={r.time_commitment_agreed ? "Yes" : "No"} />
                        <Detail label="Hosting studio affiliated" value={r.hosting_studio_affiliated ? "Yes" : "No"} />
                      </>
                    )}
                    <Detail label="Health conditions" value={r.health_notes || "—"} wide />
                    <Detail label="Injuries / surgeries" value={r.injuries_surgeries || "—"} wide />
                    <Detail label="Medications" value={r.medications || "—"} wide />
                    <Detail label="Cancellation policy agreed" value={r.cancellation_policy_agreed ? "Yes" : "No"} />
                    <Detail label="Tuition" value={tuitionPending ? "TBD (deposit-only training)" : `$${r.offering_price_usd?.toLocaleString()}`} />
                    <Detail label="Paid" value={`$${paidTotal?.toLocaleString?.() || 0}`} />
                    <Detail label="Balance due" value={balanceLabel} />
                    {r.tuition_finalized && r.tuition_finalized_at && (
                      <Detail label="Tuition finalized" value={new Date(r.tuition_finalized_at).toLocaleDateString()} />
                    )}
                    {r.last_balance_invoice_sent_at && (
                      <Detail
                        label="Last balance invoice sent"
                        value={`$${(r.last_balance_invoice_amount_usd ?? 0).toLocaleString()} on ${new Date(r.last_balance_invoice_sent_at).toLocaleDateString()}`}
                      />
                    )}
                  </div>

                  {/* Payments log */}
                  {paymentsLog.length > 0 && (
                    <div className="mt-6 bg-aviana-cream/40 rounded-xl p-4" data-testid={`payments-log-${r.id}`}>
                      <p className="overline-plum text-[10px] mb-3">Payments received</p>
                      <ul className="space-y-1.5">
                        {paymentsLog.map((p) => (
                          <li key={p.id} className="text-[12px] text-aviana-inkSoft flex items-baseline justify-between gap-4">
                            <span><strong>${p.amount_usd?.toLocaleString()}</strong> · {p.method.replace("_", " ")} {p.note ? `· ${p.note}` : ""}</span>
                            <span className="text-aviana-muted">{new Date(p.logged_at).toLocaleDateString()}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Log a payment */}
                  {r.status !== "cancelled" && r.balance_due_usd > 0 && (
                    <div className="mt-6 border border-aviana-plum/20 rounded-xl bg-aviana-plum/5 p-5" data-testid={`log-payment-section-${r.id}`}>
                      {logPaymentFor !== r.id ? (
                        <button
                          onClick={() => { setLogPaymentFor(r.id); setPaymentDraft({ amount_usd: r.balance_due_usd, method: "venmo", note: "", send_receipt_email: true, include_stripe_remainder_link: true }); }}
                          className="btn-primary !py-2 !px-5 !text-[11px]"
                          data-testid={`log-payment-${r.id}`}
                        >
                          + Log a partial / full payment
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <p className="overline-plum">Log a payment for {r.full_name.split(" ")[0]}</p>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <label className="block">
                              <span className="text-[11px] uppercase tracking-[0.18em] text-aviana-muted mb-1 block">Amount (USD)</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={paymentDraft.amount_usd}
                                onChange={(e) => setPaymentDraft({ ...paymentDraft, amount_usd: e.target.value })}
                                className="w-full bg-white border border-aviana-border rounded-lg px-3 py-2 text-[13px]"
                                data-testid={`log-amount-${r.id}`}
                              />
                            </label>
                            <label className="block">
                              <span className="text-[11px] uppercase tracking-[0.18em] text-aviana-muted mb-1 block">Method</span>
                              <select
                                value={paymentDraft.method}
                                onChange={(e) => setPaymentDraft({ ...paymentDraft, method: e.target.value })}
                                className="w-full bg-white border border-aviana-border rounded-lg px-3 py-2 text-[13px]"
                                data-testid={`log-method-${r.id}`}
                              >
                                <option value="venmo">Venmo</option>
                                <option value="zelle">Zelle</option>
                                <option value="manual_stripe">Stripe (manual)</option>
                                <option value="cash">Cash</option>
                                <option value="check">Check</option>
                              </select>
                            </label>
                          </div>
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-[0.18em] text-aviana-muted mb-1 block">Note (optional)</span>
                            <input
                              type="text"
                              value={paymentDraft.note}
                              onChange={(e) => setPaymentDraft({ ...paymentDraft, note: e.target.value })}
                              placeholder="e.g. confirmation #ABC123, sent May 19"
                              className="w-full bg-white border border-aviana-border rounded-lg px-3 py-2 text-[13px]"
                              data-testid={`log-note-${r.id}`}
                            />
                          </label>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 text-[12px] text-aviana-inkSoft">
                              <input type="checkbox" checked={paymentDraft.send_receipt_email} onChange={(e) => setPaymentDraft({ ...paymentDraft, send_receipt_email: e.target.checked })} className="accent-aviana-plum" />
                              Email receipt to {r.email}
                            </label>
                            <label className="flex items-center gap-2 text-[12px] text-aviana-inkSoft">
                              <input type="checkbox" checked={paymentDraft.include_stripe_remainder_link} onChange={(e) => setPaymentDraft({ ...paymentDraft, include_stripe_remainder_link: e.target.checked })} className="accent-aviana-plum" />
                              Include "Pay remaining via Stripe" button in email
                            </label>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={() => submitPayment(r.id)}
                              disabled={busy === r.id}
                              className="btn-primary !py-2 !px-5 !text-[11px]"
                              data-testid={`submit-log-payment-${r.id}`}
                            >
                              {busy === r.id ? "Saving…" : "Save & send email"}
                            </button>
                            <button
                              onClick={() => setLogPaymentFor(null)}
                              className="btn-secondary !py-2 !px-5 !text-[11px]"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-6 flex flex-wrap gap-2 pt-4 border-t border-aviana-border/60">
                    {r.status !== "cancelled" && (
                      <CancelRegistrationButton source="training" id={r.id} onDone={load}
                        className="btn-secondary !py-1.5 !px-4 !text-[10px] !text-red-600 !border-red-200 hover:!bg-red-500 hover:!text-white" />
                    )}
                    {["deposit_paid", "paid_in_full"].map((s) => (
                      <button
                        key={s}
                        onClick={() => updateStatus(r.id, s)}
                        disabled={busy === r.id || r.status === s}
                        className="btn-secondary !py-1.5 !px-4 !text-[10px]"
                        data-testid={`mark-${s}-${r.id}`}
                      >
                        Mark {s.replaceAll("_", " ")}
                      </button>
                    ))}
                    <button
                      onClick={() => setFinalTuition(r)}
                      disabled={busy === r.id}
                      className="btn-secondary !py-1.5 !px-4 !text-[10px] !text-aviana-goldDeep"
                      data-testid={`set-tuition-${r.id}`}
                      title="Set the final tuition amount for this registration (e.g., after headcount is confirmed for a deposit-only training)"
                    >
                      Set final tuition
                    </button>
                    {r.balance_due_usd > 0 && (
                      <button
                        onClick={() => sendBalanceInvoice(r)}
                        disabled={busy === r.id}
                        className="btn-secondary !py-1.5 !px-4 !text-[10px] !text-aviana-plum"
                        data-testid={`send-balance-invoice-${r.id}`}
                        title="Email the student a balance-due invoice with Zelle, Venmo, and Stripe payment options"
                      >
                        Send balance invoice →
                      </button>
                    )}
                    <button
                      onClick={() => sendBalanceInvoice(r, { preview: true })}
                      disabled={busy === r.id}
                      className="btn-secondary !py-1.5 !px-4 !text-[10px] !text-aviana-goldDeep"
                      data-testid={`preview-balance-invoice-${r.id}`}
                      title="Send a SAMPLE of the balance invoice email to your own inbox so you can see exactly what the student will receive"
                    >
                      👀 Preview sample to me
                    </button>
                    <a href={`mailto:${r.email}`} className="btn-secondary !py-1.5 !px-4 !text-[10px]">Email {r.full_name.split(" ")[0]}</a>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Detail({ label, value, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <p className="overline-plum text-[9px] mb-1">{label}</p>
      <p className="text-aviana-inkSoft leading-[1.6] whitespace-pre-wrap">{value}</p>
    </div>
  );
}


function CoursesTab() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/lms/courses");
        setCourses(data);
      } catch { toast.error("Could not load courses"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-aviana-muted">Loading…</div>;

  return (
    <div data-testid="admin-courses-tab">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-sm text-aviana-muted max-w-2xl">Your LearnDash courses live here. Click "Author quiz questions" to fill in the answer choices (these didn't transfer with the WordPress export).</p>
        </div>
        <Link to="/admin/lms/quizzes" className="btn-gold !py-2.5 !px-5 !text-[11px]" data-testid="open-quiz-authoring">
          Author quiz questions →
        </Link>
        <Link to="/admin/lms/submissions" className="btn-secondary !py-2.5 !px-5 !text-[11px]" data-testid="open-submissions-grading">
          Review submissions →
        </Link>
        <Link to="/admin/lms/progress" className="btn-secondary !py-2.5 !px-5 !text-[11px]" data-testid="open-student-progress">
          Student progress →
        </Link>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {courses.map((c) => (
          <div key={c.id} className="bg-aviana-cream rounded-2xl p-7 border border-aviana-border" data-testid={`admin-course-${c.slug}`}>
            <p className="overline-plum mb-3">{c.is_public ? "Public · $399" : "Bundled with in-person trainings"}</p>
            <h3 className="font-display text-2xl text-aviana-ink">{c.title}</h3>
            <div className="mt-5 flex items-center gap-8 text-[13px] text-aviana-muted">
              <span>{c.lesson_count} lessons</span>
              <span>{c.enrollment_count} enrolled</span>
            </div>
            {c.is_public && (
              <Link to={`/courses/${c.slug}`} className="mt-5 inline-block text-[11px] tracking-[0.22em] uppercase text-aviana-gold hover:text-aviana-goldDeep" target="_blank">
                View landing page →
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function JournalTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | post object
  const [busy, setBusy] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);
  const [broken, setBroken] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanMode, setScanMode] = useState("broken"); // "broken" | "all"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data);
    } catch { toast.error("Could not load posts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Scan on demand — either broken-only, or every image in every post
  // (Holly's "I fixed one, but I want to swap the one I picked" case).
  const scan = async (mode) => {
    setScanning(true);
    setScanMode(mode);
    try {
      const path = mode === "all" ? "/admin/blog/all-images" : "/admin/blog/broken-images";
      const { data } = await api.get(path);
      setBroken(data);
    } catch { toast.error("Could not scan Journal images"); }
    finally { setScanning(false); }
  };
  const scanBroken = () => scan(scanMode);

  const openRepair = async () => {
    setRepairOpen(true);
    await scan("broken");
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this essay? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/blog/${slug}`);
      toast.success("Essay deleted");
      await load();
    } catch { toast.error("Could not delete"); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <JournalEditor
        post={editing === "new" ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={async () => { setEditing(null); await load(); }}
      />
    );
  }

  return (
    <div data-testid="admin-journal">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div className="max-w-xl">
          <p className="text-sm text-aviana-muted leading-[1.75]">
            Manage every essay on the public Journal page. Add new posts for SEO (target keywords like "aerial yoga insurance", "restorative aerial yoga", "yoga teacher revenue streams"). HTML body supported — you can paste from WordPress.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={openRepair}
            className="btn-secondary inline-flex items-center gap-2 !py-2.5 !px-5 !text-[11px] !text-aviana-goldDeep"
            data-testid="journal-repair-images"
            title="Find and replace broken image links (from the WordPress export)"
          >
            <ImageIcon size={13} /> Fix broken images
          </button>
          <button
            onClick={() => setEditing("new")}
            className="btn-primary inline-flex items-center gap-2 !py-2.5 !px-5 !text-[11px]"
            data-testid="journal-new-post"
          >
            <Plus size={14} /> New essay
          </button>
        </div>
      </div>

      {repairOpen && (
        <BrokenImageRepair
          broken={broken}
          scanning={scanning}
          scanMode={scanMode}
          onScan={scan}
          onClose={() => setRepairOpen(false)}
          onRescan={scanBroken}
          onReloadPosts={load}
        />
      )}

      {loading ? <p className="text-aviana-muted">Loading…</p> : posts.length === 0 ? (
        <div className="card-aviana p-12 text-center text-aviana-muted">
          <FileText size={32} className="mx-auto mb-4 text-aviana-gold/60" />
          <p>No essays yet. Click "New essay" above to publish your first.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="bg-aviana-cream rounded-2xl border border-aviana-border p-5 flex items-center gap-5" data-testid={`journal-row-${p.slug}`}>
              {p.image_url && (
                <img src={p.image_url} alt="" className="w-20 h-20 object-cover rounded-xl ring-1 ring-aviana-border flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg text-aviana-ink truncate">{p.title}</p>
                <p className="text-[12px] text-aviana-muted mt-0.5">
                  /journal/{p.slug} · {new Date(p.published_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <a href={`/journal/${p.slug}`} target="_blank" rel="noopener noreferrer" className="btn-secondary !py-1.5 !px-3 !text-[10px]" data-testid={`journal-view-${p.slug}`}>View</a>
                <button onClick={() => setEditing(p)} className="btn-secondary !py-1.5 !px-3 !text-[10px] inline-flex items-center gap-1" data-testid={`journal-edit-${p.slug}`}>
                  <Edit3 size={11} /> Edit
                </button>
                <button onClick={() => remove(p.slug)} disabled={busy} className="btn-secondary !py-1.5 !px-3 !text-[10px] !text-red-700 hover:!bg-red-50 inline-flex items-center gap-1" data-testid={`journal-delete-${p.slug}`}>
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Broken image repair panel ────────────────────────────────────────────
// Renders every broken WP image found across the Journal, one card each.
// For each card Holly can pick a replacement from her Photo Library, upload
// a fresh photo, or remove the tag outright. Each action calls the backend
// and refreshes the scan so the list shrinks as she works through it.
function BrokenImageRepair({ broken, scanning, scanMode, onScan, onClose, onRescan, onReloadPosts }) {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(null); // key of the image currently mid-request

  useEffect(() => {
    api.get("/admin/photos").then(({ data }) => setPhotos(data)).catch(() => {});
  }, []);

  const keyOf = (b) => `${b.post_slug}::${b.image_url}`;

  const applyReplacement = async (b, newUrl) => {
    setBusy(keyOf(b));
    try {
      await api.post("/admin/blog/replace-image", {
        post_slug: b.post_slug,
        old_url: b.image_url,
        new_url: newUrl,
      });
      toast.success(newUrl ? "Image replaced" : "Image removed");
      await Promise.all([onRescan(), onReloadPosts()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update image");
    } finally {
      setBusy(null);
    }
  };

  const uploadAndReplace = async (b, file) => {
    if (!file) return;
    setBusy(keyOf(b));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", b.alt || "");
      const { data } = await api.post("/admin/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((prev) => [data, ...prev]);
      await applyReplacement(b, data.url);
    } catch {
      toast.error("Upload failed — try a smaller file or different format.");
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-aviana-ink/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 lg:p-8" data-testid="broken-image-repair-modal">
      <div className="bg-aviana-cream rounded-2xl border border-aviana-border w-full max-w-5xl my-6 shadow-2xl">
        <div className="p-6 border-b border-aviana-border flex items-start justify-between gap-4">
          <div>
            <p className="overline-plum mb-2">Journal repair</p>
            <h2 className="font-display text-3xl text-aviana-ink">
              {scanMode === "all" ? "Swap any Journal image" : "Fix broken images"}
            </h2>
            <p className="text-[13px] text-aviana-muted mt-2 max-w-2xl">
              {scanMode === "all"
                ? "Every image across every post — swap any of them for a photo from your library, upload a fresh one, or remove it entirely. Use this when you want to change an image you already fixed."
                : "Every WordPress-era image link that no longer loads. Replace them with a photo from your library, upload a fresh one, or remove the image entirely."}
            </p>
            <div className="mt-4 inline-flex bg-aviana-surface rounded-full p-1 border border-aviana-border" data-testid="scan-mode-toggle">
              <button
                onClick={() => onScan("broken")}
                className={`px-4 py-1.5 rounded-full text-[11px] tracking-[0.16em] uppercase transition-all ${scanMode === "broken" ? "bg-aviana-plum text-white" : "text-aviana-muted hover:text-aviana-ink"}`}
                data-testid="scan-mode-broken"
              >
                Broken only
              </button>
              <button
                onClick={() => onScan("all")}
                className={`px-4 py-1.5 rounded-full text-[11px] tracking-[0.16em] uppercase transition-all ${scanMode === "all" ? "bg-aviana-plum text-white" : "text-aviana-muted hover:text-aviana-ink"}`}
                data-testid="scan-mode-all"
              >
                All images
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-aviana-muted hover:text-aviana-ink p-2 -mt-1 -mr-1"
            aria-label="Close"
            data-testid="broken-image-repair-close"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-6">
          {scanning ? (
            <p className="text-aviana-muted text-center py-10">Scanning your Journal…</p>
          ) : broken.length === 0 ? (
            <div className="text-center py-14">
              <div className="inline-flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-full px-5 py-2 mb-4">
                <Check size={16} /> All clear
              </div>
              <p className="text-aviana-muted">
                {scanMode === "all" ? "No images found across your Journal posts." : "No broken images found across any Journal post. Beautiful."}
              </p>
            </div>
          ) : (
            <>
              <p className="text-[12px] text-aviana-muted mb-5">
                {scanMode === "all"
                  ? <>Showing <strong className="text-aviana-ink">{broken.length}</strong> image{broken.length === 1 ? "" : "s"} across your Journal — grouped by post.</>
                  : <>Found <strong className="text-aviana-ink">{broken.length}</strong> broken image{broken.length === 1 ? "" : "s"} to fix.</>}
              </p>
              <div className="space-y-6">
                {broken.map((b) => (
                  <BrokenImageRow
                    key={keyOf(b)}
                    row={b}
                    photos={photos}
                    busy={busy === keyOf(b)}
                    onPickFromLibrary={(url) => applyReplacement(b, url)}
                    onUpload={(file) => uploadAndReplace(b, file)}
                    onRemove={() => applyReplacement(b, "")}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BrokenImageRow({ row, photos, busy, onPickFromLibrary, onUpload, onRemove }) {
  const [mode, setMode] = useState("library"); // "library" | "upload"
  const fileRef = useRef(null);

  return (
    <div className="border border-aviana-border rounded-xl p-5 bg-white" data-testid={`broken-image-${row.post_slug}`}>
      <div className="flex items-start gap-4 mb-4">
        {/* Thumbnail preview — helps Holly visually pick out "the first image" */}
        <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-lg overflow-hidden bg-aviana-surface border border-aviana-border flex-shrink-0 relative">
          <img
            src={row.image_url}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; e.target.nextElementSibling.style.display = "flex"; }}
          />
          <div className="absolute inset-0 hidden items-center justify-center text-[10px] tracking-[0.14em] uppercase text-aviana-muted bg-aviana-surface text-center p-2">
            Broken<br/>preview
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="overline-plum">{row.post_title}</p>
            {row.position && (
              <span className="text-[10px] tracking-[0.16em] uppercase bg-aviana-gold/20 text-aviana-plumDeep rounded-full px-2 py-0.5" title="Order this image appears in the article">
                {ordinal(row.position)} image
              </span>
            )}
            {row.is_broken && (
              <span className="text-[10px] tracking-[0.16em] uppercase bg-red-100 text-red-700 rounded-full px-2 py-0.5">Broken</span>
            )}
          </div>
          <p className="text-[11px] font-mono text-aviana-muted break-all bg-aviana-surface rounded px-2 py-1 mt-2 max-w-full">
            {row.image_url}
          </p>
          {row.alt && <p className="text-[12px] text-aviana-inkSoft mt-2 italic">Alt text: "{row.alt}"</p>}
          <p className="text-[11px] text-aviana-muted mt-2">
            Appears {row.count === 1 ? "once" : `${row.count} times`} in this post — a single fix handles all copies.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-4 border-b border-aviana-border">
        {["library", "upload"].map((k) => (
          <button
            key={k}
            onClick={() => setMode(k)}
            className={`px-4 py-2 text-[11px] tracking-[0.16em] uppercase border-b-2 -mb-px ${mode === k ? "border-aviana-gold text-aviana-ink" : "border-transparent text-aviana-muted hover:text-aviana-ink"}`}
            data-testid={`broken-image-mode-${k}`}
          >
            {k === "library" ? "Pick from library" : "Upload new"}
          </button>
        ))}
      </div>

      {mode === "library" && (
        <div>
          {photos.length === 0 ? (
            <p className="text-[12px] text-aviana-muted">Your Photo Library is empty. Switch to "Upload new" to add one.</p>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-64 overflow-y-auto pr-1">
              {photos.map((p) => (
                <button
                  key={p.id}
                  disabled={busy}
                  onClick={() => onPickFromLibrary(p.url)}
                  className="relative aspect-square rounded-lg overflow-hidden ring-1 ring-aviana-border hover:ring-2 hover:ring-aviana-plum transition-all disabled:opacity-40"
                  title={p.filename}
                  data-testid={`broken-image-lib-pick-${p.id}`}
                >
                  <img src={p.url} alt={p.filename} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === "upload" && (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={(e) => onUpload(e.target.files?.[0])}
            className="hidden"
            data-testid={`broken-image-upload-${row.post_slug}`}
          />
          <button
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="btn-primary !py-2 !px-5 !text-[11px] inline-flex items-center gap-2 disabled:opacity-40"
            data-testid={`broken-image-upload-btn-${row.post_slug}`}
          >
            <Upload size={13} /> {busy ? "Uploading…" : "Choose a photo from your computer"}
          </button>
          <p className="text-[11px] text-aviana-muted mt-2">JPEG or PNG, ideally under 4MB. Gets added to your Photo Library too.</p>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-aviana-border flex justify-end">
        <button
          disabled={busy}
          onClick={() => {
            if (window.confirm("Remove this image from the article entirely? The rest of the article stays untouched.")) onRemove();
          }}
          className="text-[11px] tracking-[0.16em] uppercase text-red-700 hover:text-red-800 disabled:opacity-40 inline-flex items-center gap-1.5"
          data-testid={`broken-image-remove-${row.post_slug}`}
        >
          <Trash2 size={12} /> Remove image entirely
        </button>
      </div>
    </div>
  );
}


function JournalEditor({ post, onCancel, onSaved }) {
  const isEdit = !!post;
  const [form, setForm] = useState({
    slug: post?.slug || "",
    title: post?.title || "",
    excerpt: post?.excerpt || "",
    content_html: post?.content_html || "",
    image_url: post?.image_url || "",
    author: post?.author || "Holly Johnson",
    published_at: post?.published_at ? post.published_at.split("T")[0] : new Date().toISOString().split("T")[0],
  });
  const [busy, setBusy] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    if (!form.content_html.trim()) { toast.error("Body content is required."); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        published_at: form.published_at ? new Date(form.published_at).toISOString() : undefined,
      };
      if (isEdit) {
        await api.patch(`/admin/blog/${post.slug}`, payload);
      } else {
        await api.post("/admin/blog", payload);
      }
      toast.success(isEdit ? "Essay updated" : "Essay published");
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-aviana-border rounded-2xl p-7" data-testid="journal-editor">
      <div className="flex items-center justify-between mb-7">
        <h3 className="font-display text-2xl text-aviana-ink">{isEdit ? "Edit essay" : "New essay"}</h3>
        <button onClick={onCancel} className="text-aviana-muted hover:text-aviana-ink"><X size={20} /></button>
      </div>

      <div className="space-y-5">
        <JField label="Title *" value={form.title} onChange={(v) => update({ title: v })} testid="je-title" />
        <JField
          label="URL slug (auto-generated if blank)"
          value={form.slug}
          onChange={(v) => update({ slug: v })}
          placeholder="aerial-yoga-tips-for-beginners"
          testid="je-slug"
          hint="Used in the public URL: /journal/<slug>. Auto-generated from title if blank."
        />
        <JField label="Excerpt (1–2 sentence summary for cards & SEO)" value={form.excerpt} onChange={(v) => update({ excerpt: v })} multiline rows={2} testid="je-excerpt" />

        {/* Hero image — upload OR paste URL */}
        <div>
          <label className="overline-plum mb-2 block">Hero image</label>
          <div className="flex items-start gap-4 flex-wrap">
            {form.image_url ? (
              <div className="relative shrink-0">
                <img
                  src={form.image_url.startsWith("http") ? form.image_url : `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}${form.image_url}`}
                  alt=""
                  className="w-32 h-20 object-cover rounded-lg border border-aviana-border"
                  data-testid="je-image-preview"
                  onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                />
                <button
                  type="button"
                  onClick={() => update({ image_url: "" })}
                  className="absolute -top-2 -right-2 bg-white border border-aviana-border rounded-full p-1 text-aviana-muted hover:text-red-500"
                  data-testid="je-image-clear"
                  title="Remove image"
                ><X size={12} /></button>
              </div>
            ) : (
              <div className="w-32 h-20 rounded-lg border border-dashed border-aviana-border flex items-center justify-center text-[10px] tracking-[0.18em] uppercase text-aviana-muted">
                no image
              </div>
            )}
            <div className="flex-1 min-w-[200px] space-y-2">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 12 * 1024 * 1024) { toast.error("Max 12 MB"); return; }
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const { data } = await api.post("/admin/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    update({ image_url: data.url });
                    toast.success("Image uploaded");
                  } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
                  } finally {
                    e.target.value = "";
                  }
                }}
                className="block text-[12px] text-aviana-muted file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-aviana-plum file:text-aviana-cream file:cursor-pointer hover:file:bg-aviana-plumDeep file:text-[10px] file:tracking-[0.18em] file:uppercase"
                data-testid="je-image-upload"
              />
              <p className="text-[11px] text-aviana-muted">Or paste an existing URL ↓</p>
              <input
                type="text"
                value={form.image_url}
                onChange={(e) => update({ image_url: e.target.value })}
                placeholder="/api/files/… or https://…"
                data-testid="je-image-url"
                className="w-full bg-transparent border-b border-aviana-border focus:border-aviana-gold py-2 outline-none text-[13px] text-aviana-ink"
              />
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <JField label="Author" value={form.author} onChange={(v) => update({ author: v })} testid="je-author" />
          <JField label="Published date" type="date" value={form.published_at} onChange={(v) => update({ published_at: v })} testid="je-date" />
        </div>

        <div>
          <label className="overline-plum mb-2 block">Body (HTML)</label>
          <textarea
            value={form.content_html}
            onChange={(e) => update({ content_html: e.target.value })}
            rows={22}
            placeholder="<h2>Section heading</h2>&#10;<p>Your essay body in HTML — paragraphs, lists, headings, etc.</p>"
            className="w-full bg-aviana-cream/40 border border-aviana-border rounded-xl px-4 py-3 text-[13px] font-mono text-aviana-ink focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30 leading-[1.55]"
            data-testid="je-body"
          />
          <p className="text-[11px] text-aviana-muted mt-2">Tip: pasting from WordPress' visual editor works. You can use &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;/&lt;li&gt;, &lt;a href&gt;, &lt;blockquote&gt;, &lt;em&gt;, &lt;strong&gt;.</p>
        </div>

        <div className="flex gap-2 pt-2 border-t border-aviana-border/60">
          <button onClick={save} disabled={busy} className="btn-primary !py-2.5 !px-5 !text-[11px]" data-testid="je-save">
            {busy ? "Saving…" : isEdit ? "Update essay" : "Publish essay"}
          </button>
          <button onClick={onCancel} className="btn-secondary !py-2.5 !px-5 !text-[11px]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function JField({ label, value, onChange, type = "text", multiline = false, rows = 3, placeholder, testid, hint }) {
  return (
    <label className="block">
      <span className="overline-plum mb-2 block">{label}</span>
      {multiline ? (
        <textarea
          value={value}
          rows={rows}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-aviana-cream/40 border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink leading-[1.55] focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30"
        />
      ) : (
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-aviana-cream/40 border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30"
        />
      )}
      {hint && <p className="text-[11px] text-aviana-muted mt-1.5 leading-[1.55]">{hint}</p>}
    </label>
  );
}

function PhotosTab() {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const inputRef = useRef(null);
  const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/photos");
      setPhotos(data);
    } catch (e) { toast.error("Could not load photos"); }
  };
  useEffect(() => { load(); }, []);

  const onPick = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const f of files) {
      try {
        const prepared = await compressImage(f);
        const fd = new FormData();
        fd.append("file", prepared, prepared.name);
        await api.post("/admin/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) {
        console.error("upload failed", f.name, err);
        fail++;
      }
    }
    setBusy(false);
    e.target.value = "";
    if (ok) toast.success(`${ok} uploaded${fail ? `, ${fail} failed` : ""}`);
    else if (fail) toast.error(`Upload failed (${fail} file${fail > 1 ? "s" : ""}). Try a smaller image.`);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/admin/photos/${id}`);
      load();
    } catch { toast.error("Could not delete"); }
  };

  const copy = (id) => {
    const url = `${BACKEND_URL}/api/files/${id}`;
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
    toast.success("URL copied — paste into any offering");
  };

  return (
    <div data-testid="admin-photos">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="overline-plum">Brand photo library</p>
          <p className="text-sm text-aviana-muted mt-1 max-w-xl">Upload your own brand photos. Large images are auto-resized in your browser, so phone & DSLR shots work fine. Copy a URL and paste it onto any offering or section.</p>
        </div>
        <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" onChange={onPick} className="hidden" data-testid="photo-input" />
        <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-gold !py-2.5 !px-6 !text-[11px]" data-testid="photo-upload-btn">
          <Upload size={14} /> {busy ? "Uploading…" : "Upload photos"}
        </button>
      </div>
      {photos.length === 0 ? (
        <div className="card-aviana p-12 text-center text-aviana-muted">
          <ImageIcon size={32} className="mx-auto mb-4 text-aviana-gold/60" />
          <p>No photos yet. Click "Upload photos" to start your brand library.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {photos.map((p) => (
            <div key={p.id} className="group relative bg-aviana-cream rounded-2xl overflow-hidden border border-aviana-border" data-testid={`photo-${p.id}`}>
              <div className="aspect-square overflow-hidden">
                <img src={`${BACKEND_URL}${p.url}`} alt={p.filename} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="absolute inset-0 bg-aviana-ink/0 group-hover:bg-aviana-ink/60 transition-colors flex items-end justify-center p-4 opacity-0 group-hover:opacity-100">
                <div className="flex gap-2">
                  <button onClick={() => copy(p.id)} className="bg-aviana-cream text-aviana-ink rounded-full px-3 py-2 text-[11px] tracking-[0.18em] uppercase flex items-center gap-1.5" data-testid={`copy-${p.id}`}>
                    {copied === p.id ? <Check size={12} /> : <Copy size={12} />}
                    {copied === p.id ? "Copied" : "Copy URL"}
                  </button>
                  <button onClick={() => remove(p.id)} className="bg-red-100 text-red-800 rounded-full p-2" data-testid={`delete-${p.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <p className="text-[10px] text-aviana-muted px-3 py-2 truncate">{p.filename}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState({ ga4_id: "", show_cookie_banner: true, author_bio: "", social_preview_photo_id: "", bliss_parking_text: "" });
  const [parkingDefault, setParkingDefault] = useState("");
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/settings");
      setForm({
        ga4_id: data.ga4_id || "",
        show_cookie_banner: data.show_cookie_banner !== false,
        author_bio: data.author_bio || "",
        social_preview_photo_id: data.social_preview_photo_id || "",
        bliss_parking_text: data.bliss_parking_text || data.bliss_parking_default || "",
      });
      setParkingDefault(data.bliss_parking_default || "");
    } catch { /* keep defaults */ }
    try {
      const { data } = await api.get("/admin/photos");
      setPhotos(data);
    } catch { /* photo picker just stays empty */ }
  })(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const parkingIsDefault = form.bliss_parking_text.trim() === parkingDefault.trim();
      await api.post("/admin/settings", { ...form, bliss_parking_text: parkingIsDefault ? "" : form.bliss_parking_text });
      toast.success("Settings saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    setBusy(false);
  };
  const currentSocialPhoto = photos.find((p) => p.id === form.social_preview_photo_id);
  return (
    <div className="max-w-2xl space-y-10" data-testid="admin-settings">
      <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border p-8">
        <p className="overline-plum mb-3">Google Analytics 4</p>
        <label className="block text-sm text-aviana-muted mb-2">Measurement ID (looks like G-XXXXXXXXXX)</label>
        <input value={form.ga4_id} onChange={(e) => setForm({ ...form, ga4_id: e.target.value })}
          placeholder="G-XXXXXXXXXX"
          className="w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 outline-none text-aviana-ink"
          data-testid="ga4-input"
        />
        <p className="text-[11px] text-aviana-muted mt-3 leading-relaxed">
          Find this at analytics.google.com → Admin → Data Streams → Web → Measurement ID. We'll automatically inject the GA4 tag on every page.
        </p>
      </div>
      <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border p-8" data-testid="bliss-parking-settings">
        <p className="overline-plum mb-3">Bliss Tent parking instructions</p>
        <label className="block text-sm text-aviana-muted mb-2">Included in every confirmation email for offerings marked "Held at the Bliss Tent" and all in-person Reiki sessions. One instruction per line.</label>
        <textarea
          rows={6}
          value={form.bliss_parking_text}
          onChange={(e) => setForm({ ...form, bliss_parking_text: e.target.value })}
          className="w-full bg-white border border-aviana-border rounded-xl px-4 py-3 text-sm text-aviana-ink outline-none focus:border-aviana-plum"
          data-testid="bliss-parking-input"
        />
        {parkingDefault && (
          <button type="button" onClick={() => setForm({ ...form, bliss_parking_text: parkingDefault })}
            className="mt-2 text-[11px] tracking-[0.18em] uppercase text-aviana-muted hover:text-aviana-plum" data-testid="bliss-parking-reset">
            Reset to default
          </button>
        )}
      </div>
      <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border p-8">
        <p className="overline-plum mb-3">Cookie Banner</p>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="cookie-toggle">
          <input type="checkbox" checked={form.show_cookie_banner} onChange={(e) => setForm({ ...form, show_cookie_banner: e.target.checked })}
            className="w-5 h-5 accent-aviana-gold" />
          <span className="text-sm text-aviana-inkSoft">Show GDPR/CCPA cookie consent banner to first-time visitors</span>
        </label>
      </div>
      <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border p-8">
        <p className="overline-plum mb-3">Social preview image · Facebook, LinkedIn, iMessage</p>
        <p className="block text-sm text-aviana-muted mb-4">Shown whenever a page from your site is shared. Ideal size: 1200×630px landscape. Pick any photo from your Photo Library.</p>
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-56 aspect-[1200/630] rounded-xl bg-aviana-surface border border-aviana-border overflow-hidden flex items-center justify-center" data-testid="social-preview-current">
            {currentSocialPhoto ? (
              <img src={currentSocialPhoto.url} alt="Current social preview" className="w-full h-full object-cover" />
            ) : (
              <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted text-center px-3">Default photo<br/>(Holly · hero)</p>
            )}
          </div>
          <div className="flex-1 min-w-[200px] space-y-2">
            <button
              type="button"
              onClick={() => setShowPicker(!showPicker)}
              className="btn-secondary !py-2 !px-4 !text-[11px]"
              data-testid="social-preview-picker-toggle"
            >
              {showPicker ? "Close picker" : (currentSocialPhoto ? "Change photo" : "Pick from library")}
            </button>
            {form.social_preview_photo_id && (
              <button
                type="button"
                onClick={() => setForm({ ...form, social_preview_photo_id: "" })}
                className="block text-[11px] tracking-[0.18em] uppercase text-red-700 hover:text-red-800"
                data-testid="social-preview-reset"
              >
                Reset to default
              </button>
            )}
          </div>
        </div>
        {showPicker && (
          <div className="mt-5 pt-5 border-t border-aviana-border">
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-64 overflow-y-auto pr-1">
              {photos.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { setForm({ ...form, social_preview_photo_id: p.id }); setShowPicker(false); }}
                  className={`relative aspect-square rounded-lg overflow-hidden ring-1 transition-all ${form.social_preview_photo_id === p.id ? "ring-2 ring-aviana-plum" : "ring-aviana-border hover:ring-aviana-plum"}`}
                  title={p.filename}
                  data-testid={`social-preview-pick-${p.id}`}
                >
                  <img src={p.url} alt={p.filename} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <p className="text-[11px] text-aviana-muted mt-3">Pick landscape photos (wider than tall) for the cleanest crop. Portrait photos will be center-cropped.</p>
          </div>
        )}
        <p className="text-[11px] text-aviana-muted mt-4 leading-relaxed">
          After saving, force Facebook / LinkedIn to re-fetch the new image at{" "}
          <a href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noopener noreferrer" className="text-aviana-plum underline">Facebook Sharing Debugger</a>{" "}
          — paste your URL and click <em>Scrape Again</em> twice.
        </p>
      </div>
      <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border p-8">
        <p className="overline-plum mb-3">Author bio · Journal footer</p>
        <label className="block text-sm text-aviana-muted mb-2">Shown at the bottom of every Journal essay next to your headshot. Set the headshot at <Link to="/admin/page-images" className="text-aviana-plum underline">Page Images → Journal</Link>.</label>
        <textarea
          value={form.author_bio}
          onChange={(e) => setForm({ ...form, author_bio: e.target.value })}
          rows={5}
          placeholder="Holly Johnson is a Yoga Alliance E-RYT 500…"
          className="w-full bg-aviana-paper/40 border border-aviana-border rounded-lg p-4 outline-none focus:border-aviana-gold text-aviana-ink text-sm leading-relaxed"
          data-testid="author-bio-input"
        />
        <p className="text-[11px] text-aviana-muted mt-3">2-4 sentences works best. Plain text — line breaks preserved.</p>
      </div>
      <button onClick={save} disabled={busy} className="btn-gold" data-testid="settings-save">{busy ? "Saving…" : "Save settings"}</button>

      <ChangePasswordCard />
    </div>
  );
}

// ─── Change Password card ─────────────────────────────────────────────────────
function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. You're still signed in.");
      setCurrent(""); setNext(""); setConfirm("");
      setDone(true);
      setTimeout(() => setDone(false), 6000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-aviana-cream rounded-[2rem] border border-aviana-border p-8" data-testid="change-password-card">
      <p className="overline-plum mb-3">Account security</p>
      <h3 className="font-display text-2xl text-aviana-ink mb-2">Change your password</h3>
      <p className="text-[13px] text-aviana-muted mb-6 leading-relaxed">
        Updates take effect immediately. Your current sign-in stays active. Choose at least 8 characters — a passphrase like
        <em> "WingsAndAspens2026"</em> beats a short cryptic one.
      </p>
      <div className="space-y-5">
        <div>
          <label className="block text-[11px] tracking-[0.18em] uppercase text-aviana-muted mb-2">Current password</label>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="cp-current"
            className="w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 outline-none text-aviana-ink"
          />
        </div>
        <div>
          <label className="block text-[11px] tracking-[0.18em] uppercase text-aviana-muted mb-2">New password</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="cp-new"
            className="w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 outline-none text-aviana-ink"
          />
        </div>
        <div>
          <label className="block text-[11px] tracking-[0.18em] uppercase text-aviana-muted mb-2">Confirm new password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="cp-confirm"
            className="w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 outline-none text-aviana-ink"
          />
        </div>
      </div>
      <div className="mt-7 flex items-center gap-4 flex-wrap">
        <button type="submit" disabled={busy || !current || !next || !confirm} className="btn-gold disabled:opacity-50" data-testid="cp-submit">
          {busy ? "Updating…" : "Update password"}
        </button>
        {done && <span className="text-[12px] tracking-[0.18em] uppercase text-aviana-goldDeep" data-testid="cp-done">✓ Updated</span>}
      </div>
    </form>
  );
}


// ─── Contact Messages Tab — public contact-form submissions ───────────────
function ContactMessagesTab() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/inquiries/contact");
      setItems(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not load messages.");
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete the message from ${m.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/inquiries/contact/${m.id}`);
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Message deleted.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete message.");
    }
  };

  if (items === null) return <p className="text-aviana-muted">Loading contact messages…</p>;

  const filtered = items.filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (m.name || "").toLowerCase().includes(q)
      || (m.email || "").toLowerCase().includes(q)
      || (m.subject || "").toLowerCase().includes(q)
      || (m.message || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="admin-contact-messages">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <div>
          <p className="overline-plum">{items.length} contact {items.length === 1 ? "message" : "messages"}</p>
          <p className="text-sm text-aviana-muted mt-1">From the public `/contact` form. New messages also email holly@avianayoga.com.</p>
        </div>
        <input
          type="search"
          placeholder="Search name, email, subject…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-4 py-2.5 rounded-full bg-aviana-cream border border-aviana-border text-sm text-aviana-ink outline-none focus:border-aviana-gold transition-colors w-full sm:w-72"
          data-testid="contact-search"
        />
      </div>

      {error && <p className="text-red-700 text-sm">{error}</p>}

      {filtered.length === 0 ? (
        <p className="text-aviana-muted italic">
          {items.length === 0
            ? "No contact messages yet. As soon as visitors send via /contact, they'll appear here."
            : "No matches for your search."}
        </p>
      ) : (
        filtered.map((m) => (
          <div key={m.id} className="card-aviana p-6 md:p-7" data-testid={`contact-msg-${m.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
              <div>
                <h3 className="font-display text-2xl text-aviana-ink">{m.name}</h3>
                <p className="text-sm text-aviana-muted mt-1">
                  <a href={`mailto:${m.email}`} className="hover:text-aviana-plum">{m.email}</a>
                  {m.phone && <> · <a href={`tel:${m.phone}`} className="hover:text-aviana-plum">{m.phone}</a></>}
                </p>
              </div>
              {m.subject && (
                <span className="inline-block px-3 py-1 text-[10px] tracking-[0.2em] uppercase text-aviana-plum bg-aviana-plum/10 rounded-full">
                  {m.subject}
                </span>
              )}
            </div>
            <p className="mt-3 text-aviana-inkSoft text-[15px] leading-[1.75] whitespace-pre-wrap">
              {m.message}
            </p>
            <div className="mt-5 pt-4 border-t border-aviana-border/60 flex items-center justify-between flex-wrap gap-2 text-[11px] uppercase tracking-[0.2em] text-aviana-muted/70">
              <span>Received {new Date(m.created_at).toLocaleString()}</span>
              <div className="flex items-center gap-4">
                <a
                  href={`mailto:${m.email}?subject=${encodeURIComponent("Re: " + (m.subject || "your message"))}`}
                  className="text-aviana-plum hover:text-aviana-goldDeep normal-case tracking-normal text-[12px]"
                  data-testid={`contact-reply-${m.id}`}
                >
                  Reply via email →
                </a>
                <button
                  type="button"
                  onClick={() => handleDelete(m)}
                  className="text-red-700/80 hover:text-red-700 normal-case tracking-normal text-[12px]"
                  data-testid={`contact-delete-${m.id}`}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}


// ─── Comp Access Tab — grant LMS course access to off-platform students ───
function CompAccessTab() {
  const [courses, setCourses] = useState([]);
  const [courseSlug, setCourseSlug] = useState("");
  const [emailsRaw, setEmailsRaw] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [accessLength, setAccessLength] = useState(""); // "" = course default
  const [customDate, setCustomDate] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/lms/courses");
        setCourses(data);
        // Default to the aerial-yoga in-person modules course
        const aerial = data.find((c) => c.slug === "aerial-yoga-teacher-training-modules");
        if (aerial) setCourseSlug(aerial.slug);
        else if (data.length) setCourseSlug(data[0].slug);
      } catch (e) {
        toast.error("Could not load courses list.");
      }
    })();
  }, []);

  const parseEmails = (raw) => {
    return Array.from(new Set(
      raw.split(/[\s,;\n]+/)
        .map((e) => e.trim().toLowerCase())
        .filter((e) => /\S+@\S+\.\S+/.test(e))
    ));
  };

  const previewEmails = parseEmails(emailsRaw);
  const selectedCourse = courses.find((c) => c.slug === courseSlug);

  const submit = async (e) => {
    e.preventDefault();
    if (!courseSlug) return toast.error("Pick a course first.");
    if (previewEmails.length === 0) return toast.error("Add at least one valid email address.");
    if (accessLength === "custom" && !customDate) return toast.error("Please pick a custom expiry date.");
    const lengthLabel = {
      "": "course default",
      "5mo": "5 months from today",
      "1yr": "1 year from today",
      "lifetime": "Lifetime (no expiry)",
      "custom": `custom expiry ${customDate}`,
    }[accessLength];
    if (!window.confirm(`Grant access to "${selectedCourse?.title}" for ${previewEmails.length} ${previewEmails.length === 1 ? "student" : "students"} · ${lengthLabel}? They'll receive a login email.`)) return;
    setBusy(true);
    setResults(null);
    try {
      const payload = {
        emails: previewEmails,
        course_slug: courseSlug,
        student_note: note,
      };
      if (accessLength) payload.access_length = accessLength;
      if (accessLength === "custom") payload.access_expires_on = customDate;
      const { data } = await api.post("/admin/courses/grant-access", payload);
      setResults(data);
      toast.success(`Access granted to ${data.results.length} ${data.results.length === 1 ? "student" : "students"}.`);
      setEmailsRaw("");
      setNote("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not grant access.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-comp-access">
      <div>
        <p className="overline-plum mb-2">Grant complimentary LMS access</p>
        <p className="text-sm text-aviana-muted max-w-2xl leading-[1.65]">
          Use this to give pre-paid off-platform students access to a specific course.
          Each email gets an account (auto-created if new) plus an enrollment record for the chosen course only —
          they will <strong className="text-aviana-ink">not</strong> see any other paid course unless granted separately.
          A branded welcome email with login details is sent automatically.
        </p>
      </div>

      <form onSubmit={submit} className="bg-aviana-cream rounded-[2rem] border border-aviana-border p-8 md:p-10 space-y-6" data-testid="comp-access-form">
        <label className="block">
          <span className="overline-plum block mb-2">Course</span>
          <select
            value={courseSlug}
            onChange={(e) => setCourseSlug(e.target.value)}
            className="w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 text-aviana-ink outline-none transition-colors text-sm"
            data-testid="comp-access-course"
            required
          >
            {courses.length === 0 && <option value="">Loading courses…</option>}
            {courses.map((c) => (
              <option key={c.slug} value={c.slug}>{c.title} — /{c.slug}</option>
            ))}
          </select>
          {selectedCourse && (
            <p className="text-[12px] text-aviana-muted mt-2 italic">
              Selected: <strong className="not-italic text-aviana-ink">{selectedCourse.title}</strong>
            </p>
          )}
        </label>

        <label className="block">
          <span className="overline-plum block mb-2">Access length</span>
          <select
            value={accessLength}
            onChange={(e) => setAccessLength(e.target.value)}
            className="w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 text-aviana-ink outline-none transition-colors text-sm"
            data-testid="comp-access-length"
          >
            <option value="">Course default (5 months for in-person YTT · 1 year for online)</option>
            <option value="5mo">5 months from today</option>
            <option value="1yr">1 year from today</option>
            <option value="lifetime">Lifetime (no expiry)</option>
            <option value="custom">Custom expiry date…</option>
          </select>
          {accessLength === "custom" && (
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              className="w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 text-aviana-ink outline-none transition-colors text-sm mt-3"
              data-testid="comp-access-custom-date"
              required
            />
          )}
          <p className="text-[12px] text-aviana-muted mt-2 italic">
            Access length controls when the student loses course access. Extensions can be granted from the Users tab later.
          </p>
        </label>

        <label className="block">
          <span className="overline-plum block mb-2">Student email addresses</span>
          <textarea
            rows={5}
            value={emailsRaw}
            onChange={(e) => setEmailsRaw(e.target.value)}
            placeholder="one@example.com&#10;two@example.com&#10;three@example.com&#10;&#10;(comma, semicolon, space, or newline-separated — all work)"
            className="w-full bg-transparent border border-aviana-border rounded-xl focus:border-aviana-gold p-3 text-aviana-ink outline-none transition-colors text-sm font-mono resize-none"
            data-testid="comp-access-emails"
          />
          {previewEmails.length > 0 && (
            <p className="text-[12px] text-aviana-muted mt-2">
              <strong className="text-aviana-plum">{previewEmails.length}</strong> valid {previewEmails.length === 1 ? "email" : "emails"} detected
              {previewEmails.length <= 6 && `: ${previewEmails.join(", ")}`}
            </p>
          )}
        </label>

        <label className="block">
          <span className="overline-plum block mb-2">Internal note (optional)</span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Florida YTT May 2026 — paid via Star 4 Yoga"
            className="w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 text-aviana-ink outline-none transition-colors text-sm"
            data-testid="comp-access-note"
          />
        </label>

        <button
          type="submit"
          disabled={busy || previewEmails.length === 0 || !courseSlug}
          className="btn-gold w-full"
          data-testid="comp-access-submit"
        >
          {busy ? "Granting access…" : <>Grant access &amp; send welcome emails →</>}
        </button>
      </form>

      {results && (
        <div className="bg-aviana-cream rounded-2xl border border-aviana-gold/40 p-7" data-testid="comp-access-results">
          <p className="overline-plum mb-3 flex items-center gap-2"><Check size={14} className="text-aviana-gold" /> Access granted</p>
          <p className="text-sm text-aviana-ink mb-4">
            <strong>{results.course_title}</strong> — {results.results.length} {results.results.length === 1 ? "student" : "students"} processed.
          </p>
          <ul className="space-y-2 text-sm">
            {results.results.map((r) => (
              <li key={r.email} className="flex items-center justify-between gap-3 py-2 border-b border-aviana-border/60" data-testid={`comp-result-${r.email}`}>
                <span className="font-mono text-[13px] text-aviana-ink">{r.email}</span>
                <span className="text-[11px] tracking-[0.18em] uppercase">
                  {r.newly_enrolled
                    ? <span className="text-green-700">{r.created_user ? "New user · enrolled" : "Enrolled"}</span>
                    : <span className="text-aviana-muted">Already had access</span>}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-[12px] text-aviana-muted italic">
            Welcome emails sent via Resend. If the student doesn't see it within 5 minutes, ask them to check spam,
            or you can resend by re-submitting (the action is idempotent — no duplicate enrollments).
          </p>
        </div>
      )}
    </div>
  );
}


// ─── New Offering Dialog — quick-create form for adding events ────────────
function NewOfferingDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    type: "event",
    title: "",
    subtitle: "",
    location: "",
    start_date: "",
    end_date: "",
    price_usd: 0,
    deposit_usd: 0,
    capacity: 12,
    description: "",
    image_url: "",
    slug: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.location.trim()) {
      setError("Title and location are required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const body = {
        type: form.type,
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        location: form.location.trim(),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        price_usd: Number(form.price_usd) || 0,
        deposit_usd: Number(form.deposit_usd) || null,
        capacity: Number(form.capacity) || 12,
        description: form.description.trim(),
        image_url: form.image_url.trim(),
        slug: form.slug.trim() || undefined,
      };
      await api.post("/admin/offerings", body);
      toast.success(`${form.type[0].toUpperCase()}${form.type.slice(1)} created — click "Edit" on the new row to add photo, highlights, etc.`);
      onCreated();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not create offering.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-aviana-cream border border-aviana-border rounded-lg px-3 py-2.5 text-aviana-ink text-sm outline-none focus:border-aviana-gold transition-colors";

  return (
    <div className="fixed inset-0 bg-aviana-ink/70 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose} data-testid="new-offering-dialog">
      <div className="bg-aviana-paper rounded-2xl w-full max-w-2xl my-10" onClick={(e) => e.stopPropagation()}>
        <header className="px-7 py-5 border-b border-aviana-border bg-aviana-cream rounded-t-2xl flex items-center justify-between">
          <div>
            <p className="overline-plum mb-1">Create</p>
            <h2 className="font-display text-2xl text-aviana-ink">New offering</h2>
          </div>
          <button onClick={onClose} className="text-aviana-muted hover:text-aviana-ink" data-testid="new-offering-close">✕</button>
        </header>
        <form onSubmit={submit} className="p-7 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">{error}</div>}

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="overline-plum block mb-1.5 text-[10px]">Type</span>
              <select value={form.type} onChange={(e) => update("type", e.target.value)} className={inputCls} data-testid="no-type">
                <option value="event">Event / Workshop</option>
                <option value="retreat">Retreat</option>
                <option value="training">Teacher Training</option>
                <option value="course">Online Course (self-paced)</option>
              </select>
            </label>
            <label className="block">
              <span className="overline-plum block mb-1.5 text-[10px]">Capacity</span>
              <input type="number" min="1" value={form.capacity} onChange={(e) => update("capacity", e.target.value)} className={inputCls} data-testid="no-capacity" />
            </label>
          </div>

          <label className="block">
            <span className="overline-plum block mb-1.5 text-[10px]">Title *</span>
            <input required value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g. Solstice Sound Bath in the Garden" className={inputCls} data-testid="no-title" />
          </label>

          <label className="block">
            <span className="overline-plum block mb-1.5 text-[10px]">Subtitle / tagline</span>
            <input value={form.subtitle} onChange={(e) => update("subtitle", e.target.value)} placeholder="A 2-hour journey into stillness" className={inputCls} data-testid="no-subtitle" />
          </label>

          <label className="block">
            <span className="overline-plum block mb-1.5 text-[10px]">Location *</span>
            <input required value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Sugar Grove, IL or 'Online'" className={inputCls} data-testid="no-location" />
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="overline-plum block mb-1.5 text-[10px]">Start date</span>
              <input type="date" value={form.start_date} onChange={(e) => update("start_date", e.target.value)} className={inputCls} data-testid="no-start" />
            </label>
            <label className="block">
              <span className="overline-plum block mb-1.5 text-[10px]">End date</span>
              <input type="date" value={form.end_date} onChange={(e) => update("end_date", e.target.value)} className={inputCls} data-testid="no-end" />
            </label>
          </div>
          <p className="text-[11px] text-aviana-muted -mt-3 italic">
            Leave blank for self-paced offerings (online courses). End date defaults to start date for single-day events.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="overline-plum block mb-1.5 text-[10px]">Price (USD) *</span>
              <input type="number" required min="0" value={form.price_usd} onChange={(e) => update("price_usd", e.target.value)} className={inputCls} data-testid="no-price" />
            </label>
            <label className="block">
              <span className="overline-plum block mb-1.5 text-[10px]">Deposit (optional)</span>
              <input type="number" min="0" value={form.deposit_usd} onChange={(e) => update("deposit_usd", e.target.value)} placeholder="e.g. 500" className={inputCls} data-testid="no-deposit" />
            </label>
          </div>

          <label className="block">
            <span className="overline-plum block mb-1.5 text-[10px]">Short description</span>
            <textarea rows={3} value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="A brief paragraph — you can flesh this out later in Edit." className={`${inputCls} resize-none`} data-testid="no-description" />
          </label>

          <label className="block">
            <span className="overline-plum block mb-1.5 text-[10px]">Slug (optional · auto from title)</span>
            <input value={form.slug} onChange={(e) => update("slug", e.target.value)} placeholder="solstice-sound-bath-garden" className={`${inputCls} font-mono text-xs`} data-testid="no-slug" />
            <p className="text-[11px] text-aviana-muted mt-1.5">Used in the URL: <code className="text-aviana-plum">/offering/{form.slug || "your-slug-here"}</code></p>
          </label>

          <div className="bg-aviana-cream/60 rounded-lg p-4 text-[12px] text-aviana-muted italic border border-aviana-border/60">
            💡 After creating, click <strong className="not-italic text-aviana-ink">Edit</strong> on the new row to upload a real hero photo, add highlights, set special tuition, etc.
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-aviana-border">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-5">Cancel</button>
            <button type="submit" disabled={busy} className="btn-gold !py-2.5 !px-6" data-testid="no-submit">
              {busy ? "Creating…" : "Create offering"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Need Check icon — already imported at top of file


// Event Registrations — a flat list of everyone who signed up for a day
// event / sister circle / workshop. Prioritizes phone + email visibility so
// Holly can quickly reach out if she needs to cancel or reschedule an
// under-registered event.
function EventRegistrationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRow, setOpenRow] = useState(null);
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/admin/event-registrations");
      setRows(data);
    } catch { toast.error("Could not load event registrations"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (r, payload, msg) => {
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      await api.patch(`/admin/event-registrations/${r.id}`, payload);
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy((b) => ({ ...b, [r.id]: false }));
    }
  };

  const markPaid = (r) => {
    const amt = window.prompt("Amount paid (via Venmo/Zelle/cash/etc):", String(r.price_usd || ""));
    if (amt == null || amt === "") return;
    patch(r, { status: "paid", amount_paid_usd: parseFloat(amt) }, "Marked paid");
  };

  // Group by offering so a low-registration event is a single glance.
  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const k = r.offering_slug;
      (g[k] = g[k] || { title: r.offering_title, slug: k, items: [] }).items.push(r);
    }
    return Object.values(g).sort((a, b) => a.title.localeCompare(b.title));
  }, [rows]);

  if (loading) return <p className="text-aviana-muted">Loading…</p>;
  if (rows.length === 0) return <p className="text-aviana-muted italic">No event registrations yet.</p>;

  return (
    <div className="space-y-8" data-testid="admin-event-registrations">
      {grouped.map((g) => (
        <div key={g.slug} className="rounded-2xl bg-white border border-aviana-border p-6">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
            <div>
              <p className="overline-plum mb-1">{g.items.length} {g.items.length === 1 ? "registration" : "registrations"}</p>
              <h3 className="font-display text-2xl text-aviana-ink">{g.title}</h3>
            </div>
            <Link to={`/offering/${g.slug}`} target="_blank" className="text-[11px] tracking-[0.22em] uppercase text-aviana-plum hover:text-aviana-gold">
              View public page →
            </Link>
          </div>
          <div className="divide-y divide-aviana-border/60">
            {g.items.map((r) => (
              <div key={r.id} className="py-3" data-testid={`event-reg-${r.id}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-aviana-ink font-medium">
                      {r.full_name}
                      {r.status === "pending_payment" && <span className="ml-2 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Pending payment</span>}
                      {r.status === "pending_manual_payment" && <span className="ml-2 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Awaiting {r.payment_method || "manual"}</span>}
                      {r.status === "paid" && <span className="ml-2 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Paid</span>}
                      {r.status === "cancelled" && <span className="ml-2 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-700">Cancelled</span>}
                    </p>
                    <p className="text-[13px] text-aviana-inkSoft">
                      <a href={`mailto:${r.email}`} className="hover:text-aviana-plum">{r.email}</a>
                      <span className="text-aviana-muted mx-2">·</span>
                      <a href={`tel:${r.phone}`} className="hover:text-aviana-plum">{r.phone}</a>
                    </p>
                  </div>
                  <button onClick={() => setOpenRow(openRow === r.id ? null : r.id)} className="text-[11px] tracking-[0.18em] uppercase text-aviana-plum hover:text-aviana-gold" data-testid={`event-reg-expand-${r.id}`}>
                    {openRow === r.id ? "Hide" : "Full intake"} →
                  </button>
                </div>
                {r.status !== "cancelled" && (
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                    {r.status !== "paid" && (
                      <button
                        onClick={() => markPaid(r)}
                        disabled={busy[r.id]}
                        className="px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-700 hover:bg-emerald-500 hover:text-white transition-colors"
                        data-testid={`event-reg-mark-paid-${r.id}`}
                      >
                        Mark paid (Venmo/Zelle)
                      </button>
                    )}
                    <CancelRegistrationButton source="event" id={r.id} onDone={load} />
                  </div>
                )}
                {openRow === r.id && (
                  <div className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-[13px] text-aviana-inkSoft bg-aviana-surface/50 rounded-lg p-4">
                    <p><strong className="text-aviana-ink">Emergency contact:</strong> {r.emergency_contact_name} — {r.emergency_contact_phone}</p>
                    <p><strong className="text-aviana-ink">How heard:</strong> {r.how_heard || "—"}</p>
                    <p className="sm:col-span-2"><strong className="text-aviana-ink">Physical limitations:</strong> {r.physical_limitations || "none"}</p>
                    <p><strong className="text-aviana-ink">Liability waiver:</strong> {r.liability_waiver_agreed ? "✓ Agreed" : "—"}</p>
                    <p><strong className="text-aviana-ink">Photo release:</strong> {r.photo_waiver_agreed ? "✓ Agreed" : "—"}</p>
                    <p><strong className="text-aviana-ink">Newsletter opt-in:</strong> {r.subscribed_to_newsletter ? "✓ Subscribed" : "—"}</p>
                    <p><strong className="text-aviana-ink">Registered:</strong> {new Date(r.created_at).toLocaleString()}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
